/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  NoteCard, 
  NOTE_COLORS, 
  ChecklistItem, 
  Notebook, 
  NoteColor,
  DESK_THEMES,
  DeskTheme
} from '../../types';
import { SketchPad } from '../canvas/SketchPad';
import { useToast } from '../ui/ToastContext';
import { AudioRecorder } from './AudioRecorder';
import { 
  X, 
  Maximize2, 
  Minimize2, 
  Undo, 
  Redo, 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough, 
  List, 
  ListOrdered, 
  CheckSquare, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Link, 
  Quote, 
  Code, 
  Sparkles, 
  Clock, 
  Palette, 
  Tag, 
  Lock, 
  Unlock, 
  Copy, 
  Trash2, 
  Printer, 
  Info, 
  Plus, 
  Check, 
  Search, 
  Users, 
  ChevronDown, 
  Calendar, 
  FileText, 
  Share2, 
  History, 
  Activity, 
  Grid,
  Type,
  Highlighter,
  Eraser,
  FileDown,
  ClipboardCopy,
  MoreHorizontal,
  Heading,
  Hash,
  Minus
} from 'lucide-react';

interface NoteEditorInlineProps {
  card: Partial<NoteCard>;
  onSave: (updatedCard: Partial<NoteCard>) => void;
  onCancel: () => void;
  allTags: string[];
  onDelete: (id: string, e: React.MouseEvent) => void;
  onDuplicate: (card: NoteCard, e: React.MouseEvent) => void;
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
  setIsNotebooksSidebarCollapsed?: (collapsed: boolean) => void;
  setIsPagesSidebarCollapsed?: (collapsed: boolean) => void;
  notebooks?: Notebook[];
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

const TABLE_PICKER_ROWS = 5;
const TABLE_PICKER_COLS = 10;

const PASTEL_COLORS = [
  { name: 'Yellow', fill: '#FFF4B8', text: '#5F521E', border: '#D9C873' },
  { name: 'Pink', fill: '#FFD6E8', text: '#662B44', border: '#D998B5' },
  { name: 'Blue', fill: '#D6F4FF', text: '#1F4759', border: '#9BCDE0' },
  { name: 'Green', fill: '#DDF7D5', text: '#2A4D22', border: '#A4D998' },
  { name: 'Purple', fill: '#E8DEFF', text: '#3E2F66', border: '#B5A0E6' },
  { name: 'Orange', fill: '#FFE3D1', text: '#663B1F', border: '#E6A780' },
  { name: 'Peach', fill: '#FFEAD2', text: '#66451F', border: '#E6B380' },
  { name: 'Mint', fill: '#E2FCEF', text: '#1E593D', border: '#A2E6C3' },
  { name: 'Lavender', fill: '#F0E5FF', text: '#4D2F66', border: '#C9A0E6' },
  { name: 'Sky Blue', fill: '#E6F4FE', text: '#214966', border: '#A0CAE6' },
];

export const NoteEditorInline: React.FC<NoteEditorInlineProps> = ({
  card,
  onSave,
  onCancel,
  allTags = [],
  onDelete,
  onDuplicate,
  onToggleFavorite,
  setIsNotebooksSidebarCollapsed,
  setIsPagesSidebarCollapsed,
  notebooks = [],
  isFullscreen: parentIsFullscreen,
  onToggleFullscreen
}) => {
  const { showToast, confirm } = useToast();

  // Core parameters
  const [title, setTitle] = useState(card.title || '');
  const [tags, setTags] = useState<string[]>(card.tags || []);
  const [tagInput, setTagInput] = useState('');
  
  // Custom design parameters
  const [deskThemeIdx, setDeskThemeIdx] = useState<number>(() => {
    if (card.deskThemeIdx !== undefined) return card.deskThemeIdx;
    const saved = localStorage.getItem(`notebook_clone_desk_theme_idx_${card.id}`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const getActiveTheme = (): DeskTheme => {
    const boardColor = card.boardColor;
    if (boardColor) {
      // 1. Try matching pastel colors
      const pastelMatch = PASTEL_COLORS.find(
        (p) => p.fill.toLowerCase() === boardColor.toLowerCase()
      );
      if (pastelMatch) {
        return {
          name: pastelMatch.name,
          bgValue: pastelMatch.fill,
          textValue: pastelMatch.text,
          accentBorder: pastelMatch.border,
          buttonHoverBg: 'rgba(0,0,0,0.06)',
          toolbarBg: 'rgba(255,255,255,0.85)',
          metaBadgeBg: 'rgba(0,0,0,0.05)'
        };
      }
      // 2. Try matching desk themes
      const themeMatch = DESK_THEMES.find(
        (t) => t.bgValue.toLowerCase() === boardColor.toLowerCase()
      );
      if (themeMatch) {
        return themeMatch;
      }
      // 3. Fallback for custom hex
      return {
        name: 'Custom',
        bgValue: boardColor,
        textValue: '#1f2421',
        accentBorder: 'rgba(0,0,0,0.15)',
        buttonHoverBg: 'rgba(0,0,0,0.06)',
        toolbarBg: 'rgba(255,255,255,0.85)',
        metaBadgeBg: 'rgba(0,0,0,0.05)'
      };
    }
    return DESK_THEMES[deskThemeIdx] || DESK_THEMES[0];
  };

  const activeTheme = getActiveTheme();

  useEffect(() => {
    localStorage.setItem(`notebook_clone_desk_theme_idx_${card.id}`, deskThemeIdx.toString());
  }, [deskThemeIdx, card.id]);

  // Auto saving indicator status
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'ready'>('ready');

  // Alarms Reminders metadata
  const [reminderTime, setReminderTime] = useState<string>(
    card.reminderTime ? new Date(card.reminderTime).toISOString().substring(0, 16) : ''
  );
  
  // Contents by card type
  const [textContent, setTextContent] = useState(card.textContent || '');
  const [checklist, setChecklist] = useState<ChecklistItem[]>(card.checklistContent || []);
  const [newItemText, setNewItemText] = useState('');
  const [sketchDataUrl, setSketchDataUrl] = useState(card.sketchDataUrl || '');
  const [audioUrl, setAudioUrl] = useState(card.audioUrl || '');
  const [audioDuration, setAudioDuration] = useState(card.audioDuration || 0);
  const [photoUrl, setPhotoUrl] = useState(card.photoUrl || '');

  // Sub menu / panel configurations state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isFullscreenActive = parentIsFullscreen !== undefined ? parentIsFullscreen : isFullscreen;
  const [showBrushSelector, setShowBrushSelector] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [showReminderConfig, setShowReminderConfig] = useState(false);
  
  // Bottom-right 3-Dots Scrollable Options Modal popover
  const [showMenuPopover, setShowMenuPopover] = useState(false);

  // Companion dialog triggers for the 12 items
  const [activeDialog, setActiveDialog] = useState<
    'info' | 'versions' | 'activities' | 'merge' | 'lock' | 'associated_links' | 'search' | null
  >(null);

  // 1. Info Dialog data sets
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  // 2. Versions - local version tracking snapshots
  interface NoteSnapshot {
    id: string;
    timestamp: number;
    title: string;
    textContent: string;
    checklist: ChecklistItem[];
  }
  const [versionSnapshots, setVersionSnapshots] = useState<NoteSnapshot[]>([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);

  // 3. Activity log Ledger tracking
  interface ActivityItem {
    timestamp: number;
    text: string;
  }
  const [activityLogs, setActivityLogs] = useState<ActivityItem[]>([
    { timestamp: Date.now(), text: 'Opened document in write workspace.' }
  ]);

  // Session elapsed dur tracker
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  // 5. Merge Notes list
  const [availableNotesForMerge, setAvailableNotesForMerge] = useState<NoteCard[]>([]);
  const [selectedNoteIdToMerge, setSelectedNoteIdToMerge] = useState<string | null>(null);

  // 6. Security Lock PIN systems
  const [isLocked, setIsLocked] = useState(card.isLocked || false);
  const [pinCode, setPinCode] = useState(card.pinCode || '');
  const [lockPinInput, setLockPinInput] = useState('');

  // 10. Associated Links references list
  const [associatedLinks, setAssociatedLinks] = useState<string[]>([]);
  const [newLinkLabel, setNewLinkLabel] = useState('');

  // 11. Search overlays states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<number>(0);

  // Editor styling states
  const [fontFamily, setFontFamily] = useState('"Inter", sans-serif');
  const [fontSize, setFontSize] = useState(16);
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const initialContentRef = useRef(card.textContent || '');

  // Rich-Text Editor Custom History Stack (Undo/Redo)
  const historyStackRef = useRef<string[]>([card.textContent || '']);
  const historyIndexRef = useRef<number>(0);
  const historyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track Undo/Redo availability as React state to trigger re-renders
  const [canUndoState, setCanUndoState] = useState(false);
  const [canRedoState, setCanRedoState] = useState(false);

  // Active formattings state detection
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    alignLeft: true,
    alignCenter: false,
    alignRight: false,
    alignJustify: false,
    bulletList: false,
    orderedList: false,
    code: false,
  });

  // Selection helpers to ensure we never lose focus or cursor position
  const ensureSelectionInEditor = () => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection) return;
    
    let isInside = false;
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      isInside = editorRef.current.contains(range.startContainer);
    }
    
    if (!isInside) {
      if (savedRangeRef.current && editorRef.current.contains(savedRangeRef.current.startContainer)) {
        restoreSelection();
      } else {
        editorRef.current.focus();
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false); // collapse/place cursor at end
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.startContainer)) {
        savedRangeRef.current = range;
      }
    }
  };

  const restoreSelection = () => {
    if (savedRangeRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }
    }
  };

  // Helper to coordinate all rich text clicks preventing selection drop or focus loss
  const handleToolbarAction = (e: React.SyntheticEvent, action: () => void) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      
      console.log("[Rich Editor Debug] handleToolbarAction fired");
      
      // Focus if not already focused
      if (editorRef.current && document.activeElement !== editorRef.current) {
        editorRef.current.focus();
      }

      // Ensure we have a valid selection inside the editor
      ensureSelectionInEditor();

      action();

      if (editorRef.current) {
        editorRef.current.focus();
      }

      // Fast update states to reflect changes on styling buttons immediately
      updateActiveStates();
      updateActiveTableSelection();
    } catch (err) {
      console.error("[Rich Editor Debug] Helper toolbar action failing: ", err);
    }
  };

  const touchMouseActions = (action: () => void) => ({
    onMouseDown: (e: React.SyntheticEvent) => handleToolbarAction(e, action),
    onTouchStart: (e: React.SyntheticEvent) => handleToolbarAction(e, action)
  });

  // Helper to find the first text node in a parent element recursively
  const findFirstTextNode = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) return node;
    for (let i = 0; i < node.childNodes.length; i++) {
      const found = findFirstTextNode(node.childNodes[i]);
      if (found) return found;
    }
    return null;
  };

  // Helper to get Range for the word at the cursor position
  const getWordRangeAtCursor = (): Range | null => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        return range;
      }

      let node = range.startContainer;
      let offset = range.startOffset;

      if (!editorRef.current || !editorRef.current.contains(node)) {
        return null;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        if (element.childNodes.length > 0) {
          const child = element.childNodes[Math.min(offset, element.childNodes.length - 1)];
          if (child && child.nodeType === Node.TEXT_NODE) {
            node = child;
            offset = 0;
          } else {
            const textNode = findFirstTextNode(child || element);
            if (textNode) {
              node = textNode;
              offset = 0;
            } else {
              return null;
            }
          }
        } else {
          return null;
        }
      }

      if (node.nodeType !== Node.TEXT_NODE) return null;

      const text = node.textContent || '';
      const isBoundary = (char: string) => {
        if (!char) return true;
        return /[\s\.,;:!\?"'\(\)\[\]\{\}<>\*\-+=\/\\|`~@#\$%\^&]/.test(char);
      };

      const leftChar = offset > 0 ? text[offset - 1] : '';
      const rightChar = offset < text.length ? text[offset] : '';

      if (isBoundary(leftChar) && isBoundary(rightChar)) {
        return null; // Cursor strictly between words, don't auto-select
      }

      let start = offset;
      let end = offset;

      while (start > 0 && !isBoundary(text[start - 1])) {
        start--;
      }
      while (end < text.length && !isBoundary(text[end])) {
        end++;
      }

      if (start === end) return null;

      const newRange = document.createRange();
      newRange.setStart(node, start);
      newRange.setEnd(node, end);
      return newRange;
    } catch (e) {
      console.warn("Failed to get word range at cursor: ", e);
      return null;
    }
  };

  // Helper to select the current sentence at cursor
  const selectSentenceAtCursor = () => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      let node = range.startContainer;
      let offset = range.startOffset;

      if (!editorRef.current || !editorRef.current.contains(node)) {
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const textNode = findFirstTextNode(node);
        if (textNode) {
          node = textNode;
          offset = 0;
        } else {
          return;
        }
      }

      if (node.nodeType !== Node.TEXT_NODE) return;

      const text = node.textContent || '';

      // Walk left to find sentence start (. ! ? followed by space)
      let start = offset;
      while (start > 0) {
        const char = text[start - 1];
        if ((char === '.' || char === '!' || char === '?') && (start === text.length || /\s/.test(text[start]))) {
          break;
        }
        start--;
      }
      // Skip leading whitespace
      while (start < offset && /\s/.test(text[start])) {
        start++;
      }

      // Walk right to find sentence end (. ! ?)
      let end = offset;
      while (end < text.length) {
        const char = text[end];
        if (char === '.' || char === '!' || char === '?') {
          end++;
          break;
        }
        end++;
      }

      if (start < end) {
        const newRange = document.createRange();
        newRange.setStart(node, start);
        newRange.setEnd(node, end);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    } catch (e) {
      console.warn("Failed to select sentence at cursor: ", e);
    }
  };

  // Helper to select current paragraph at cursor
  const selectParagraphAtCursor = () => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      let node = range.startContainer;

      if (!editorRef.current || !editorRef.current.contains(node)) {
        return;
      }

      const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'LI', 'TD', 'BLOCKQUOTE', 'PRE'];
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && blockTags.includes((node as Element).tagName)) {
          break;
        }
        node = node.parentNode || editorRef.current!;
      }

      if (node) {
        const newRange = document.createRange();
        newRange.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    } catch (e) {
      console.warn("Failed to select paragraph at cursor: ", e);
    }
  };

  const toggleInlineCode = () => {
    try {
      ensureSelectionInEditor();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);

      if (!editorRef.current || !editorRef.current.contains(range.startContainer)) {
        return;
      }

      if (range.collapsed) {
        const wordRange = getWordRangeAtCursor();
        if (wordRange) {
          const originalContainer = range.startContainer;
          const originalOffset = range.startOffset;
          const wordStartContainer = wordRange.startContainer;
          const wordStartOffset = wordRange.startOffset;
          const relativeOffset = originalOffset - wordStartOffset;

          selection.removeAllRanges();
          selection.addRange(wordRange);

          const activeRange = selection.getRangeAt(0);
          const selectedText = activeRange.toString();
          if (selectedText) {
            let parent = activeRange.commonAncestorContainer;
            if (parent.nodeType === Node.TEXT_NODE) {
              parent = parent.parentNode!;
            }
            if (parent && (parent as HTMLElement).tagName === 'CODE') {
              const parentEl = parent as HTMLElement;
              const textNode = document.createTextNode(parentEl.textContent || '');
              parentEl.parentNode?.replaceChild(textNode, parentEl);
              
              const restoreRange = document.createRange();
              restoreRange.setStart(textNode, relativeOffset);
              restoreRange.setEnd(textNode, relativeOffset);
              selection.removeAllRanges();
              selection.addRange(restoreRange);
            } else {
              const codeNode = document.createElement('code');
              codeNode.className = "bg-slate-100 dark:bg-zinc-800/80 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded-md font-mono text-xs select-text border border-slate-200 dark:border-zinc-700 inline-block font-semibold";
              codeNode.textContent = selectedText;
              
              activeRange.deleteContents();
              activeRange.insertNode(codeNode);

              const textChild = codeNode.firstChild;
              if (textChild) {
                const restoreRange = document.createRange();
                const targetOffset = Math.min(relativeOffset, textChild.textContent?.length || 0);
                restoreRange.setStart(textChild, targetOffset);
                restoreRange.setEnd(textChild, targetOffset);
                selection.removeAllRanges();
                selection.addRange(restoreRange);
              }
            }
          }
        }
      } else {
        const selectedText = range.toString();
        if (!selectedText) return;

        let parent = range.commonAncestorContainer;
        if (parent.nodeType === Node.TEXT_NODE) {
          parent = parent.parentNode!;
        }
        
        if (parent && (parent as HTMLElement).tagName === 'CODE') {
          const parentEl = parent as HTMLElement;
          const textNode = document.createTextNode(parentEl.textContent || '');
          parentEl.parentNode?.replaceChild(textNode, parentEl);
        } else {
          const html = `<code class="bg-slate-100 dark:bg-zinc-800/80 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded-md font-mono text-xs select-text border border-slate-200 dark:border-zinc-700 inline-block font-semibold">${selectedText}</code>`;
          document.execCommand('insertHTML', false, html);
        }
      }

      handleContentInput();
      updateActiveStates();
      if (editorRef.current) {
        pushHistoryState(editorRef.current.innerHTML);
      }
    } catch (e) {
      console.warn("Failed to toggle inline code: ", e);
    }
  };

  // Helper to fetch other note cards catalog
  const getNotesCatalog = (): NoteCard[] => {
    try {
      const raw = localStorage.getItem('notebook_clone_cards_catalog');
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      console.error("Failed to parse notes catalog:", e);
    }
    return [];
  };

  // Synchronize internal note links titles if they changed
  const syncInternalNoteLinks = (htmlContent: string): string => {
    if (!htmlContent) return htmlContent;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const noteLinks = doc.querySelectorAll('a[data-note-link="true"]');
      if (noteLinks.length === 0) return htmlContent;

      const catalog = getNotesCatalog();
      let changed = false;

      noteLinks.forEach((linkEl) => {
        const noteId = linkEl.getAttribute('data-note-id');
        if (noteId) {
          const targetNote = catalog.find(n => n.id === noteId);
          if (targetNote) {
            const expectedTitle = targetNote.title || 'Untitled Note';
            if (linkEl.textContent !== expectedTitle) {
              linkEl.textContent = expectedTitle;
              changed = true;
            }
          }
        }
      });

      return changed ? doc.body.innerHTML : htmlContent;
    } catch (e) {
      console.warn("Failed to sync note links:", e);
      return htmlContent;
    }
  };

  // Update backlink references for this note
  const updateBacklinks = () => {
    if (!card.id) return;
    try {
      const catalog = getNotesCatalog();
      const found: BacklinkItem[] = [];
      catalog.forEach((n) => {
        if (n.id !== card.id && n.textContent && n.textContent.includes(`data-note-id="${card.id}"`)) {
          found.push({
            id: n.id,
            title: n.title || 'Untitled Note'
          });
        }
      });
      setBacklinks(found);
    } catch (e) {
      console.warn("Failed to update backlinks:", e);
    }
  };

  // Generate interactive video/app embeds or site card links
  const getUrlEmbedHtml = (url: string): string => {
    const cleanUrl = url.trim();
    
    // YouTube RegExp
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const ytMatch = cleanUrl.match(youtubeRegex);
    if (ytMatch) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full aspect-video rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/${ytMatch[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">YouTube Video Embed</p>
      </div><p><br></p>`;
    }

    // Loom RegExp
    const loomRegex = /loom\.com\/share\/([a-zA-Z0-9_-]+)/;
    const loomMatch = cleanUrl.match(loomRegex);
    if (loomMatch) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full aspect-video rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="https://www.loom.com/embed/${loomMatch[1]}" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">Loom Video Embed</p>
      </div><p><br></p>`;
    }

    // Figma RegExp
    if (cleanUrl.includes('figma.com/file/') || cleanUrl.includes('figma.com/proto/')) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full aspect-video rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(cleanUrl)}" allowfullscreen></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">Figma Embed</p>
      </div><p><br></p>`;
    }

    // Google Docs RegExp
    if (cleanUrl.includes('docs.google.com/document/d/')) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full h-96 rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="${cleanUrl}${cleanUrl.includes('?') ? '&' : '?'}embedded=true"></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">Google Docs Embed</p>
      </div><p><br></p>`;
    }

    // CodePen RegExp
    const codepenRegex = /codepen\.io\/([^\/\s]+)\/pen\/([a-zA-Z0-9]+)/;
    const cpMatch = cleanUrl.match(codepenRegex);
    if (cpMatch) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full h-80 rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="https://codepen.io/${cpMatch[1]}/embed/${cpMatch[2]}?default-tab=html%2Cresult" loading="lazy" allowtransparency="true" allowfullscreen="true"></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">CodePen Embed</p>
      </div><p><br></p>`;
    }

    // Canva RegExp
    if (cleanUrl.includes('canva.com/design/')) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full aspect-video rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="${cleanUrl}/view?embed" allowfullscreen></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">Canva Embed</p>
      </div><p><br></p>`;
    }

    // Miro RegExp
    if (cleanUrl.includes('miro.com/app/board/')) {
      return `<div class="my-4 text-center" contenteditable="false">
        <div class="relative w-full aspect-video rounded-xl overflow-hidden border shadow-sm max-w-lg mx-auto">
          <iframe class="absolute inset-0 w-full h-full" src="${cleanUrl.replace('/board/', '/app/embed/')}" allowfullscreen></iframe>
        </div>
        <p class="text-[10px] text-slate-400 mt-1.5 font-medium">Miro Embed</p>
      </div><p><br></p>`;
    }

    // Default web site card preview
    return `<div class="my-4 text-center" contenteditable="false">
      <a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-3.5 bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-left w-full max-w-md mx-auto hover:bg-slate-100 transition-colors shadow-2xs font-sans">
        <span class="text-xl font-normal">🌐</span>
        <div class="min-w-0 flex-1">
          <div class="text-xs font-bold text-slate-800 truncate">${cleanUrl}</div>
          <div class="text-[9.5px] text-slate-400 font-medium truncate mt-0.5">Click to open link in a new browser tab</div>
        </div>
      </a>
    </div><p><br></p>`;
  };

  // Insert internal note link
  const handleInsertNoteLink = (targetCard: NoteCard) => {
    restoreSelection();
    const noteId = targetCard.id;
    const noteTitle = targetCard.title || 'Untitled Note';
    const html = `<a href="#note-${noteId}" data-note-link="true" data-note-id="${noteId}" class="text-indigo-600 dark:text-indigo-400 hover:underline font-bold" onclick="event.preventDefault(); window.dispatchEvent(new CustomEvent('open-note', { detail: { id: '${noteId}' } }));">${noteTitle}</a>`;
    insertHtmlAtCursor(html);
    setIsNoteLinkModalOpen(false);
    setNoteLinkSearchQuery('');
    savedRangeRef.current = null;
    logActivity(`Inserted note link reference to "${noteTitle}".`);
  };

  // Insert rich embed block
  const handleInsertEmbed = () => {
    if (!embedUrl.trim()) {
      setEmbedError('URL is required');
      return;
    }
    setEmbedError('');
    restoreSelection();
    const html = getUrlEmbedHtml(embedUrl);
    insertHtmlAtCursor(html);
    setIsEmbedModalOpen(false);
    setEmbedUrl('');
    savedRangeRef.current = null;
    logActivity(`Inserted URL embed block.`);
  };

  // Trigger modals with saved selection range
  const openNoteLinkModal = () => {
    ensureSelectionInEditor();
    saveSelection();
    setIsNoteLinkModalOpen(true);
  };

  const openEmbedModal = () => {
    ensureSelectionInEditor();
    saveSelection();
    setIsEmbedModalOpen(true);
  };

  // Resizes active column proportionally preserving 100% total table width
  const resizeCurrentColumn = (increase: boolean) => {
    if (!activeCellElement || !activeTableElement) return;
    try {
      const colIndex = activeCellElement.cellIndex;
      const amount = increase ? 5 : -5;
      const rows = activeTableElement.rows;
      if (rows.length === 0) return;

      const firstRow = rows[0];
      const colCount = firstRow.cells.length;
      if (colCount <= 1) return;

      const widths = Array.from(firstRow.cells).map((cell) => {
        const wStr = (cell as HTMLTableCellElement).style.width || '';
        const parsed = parseFloat(wStr);
        return isNaN(parsed) ? (100 / colCount) : parsed;
      });

      const currentWidth = widths[colIndex];
      const newWidth = Math.max(10, Math.min(80, currentWidth + amount));
      const widthDiff = newWidth - currentWidth;

      widths[colIndex] = newWidth;
      const remainingSum = widths.reduce((sum, w, idx) => idx === colIndex ? sum : sum + w, 0);
      
      for (let i = 0; i < colCount; i++) {
        if (i !== colIndex) {
          if (remainingSum > 0) {
            widths[i] = widths[i] - (widthDiff * (widths[i] / remainingSum));
          } else {
            widths[i] = (100 - newWidth) / (colCount - 1);
          }
          widths[i] = Math.max(5, widths[i]);
        }
      }

      const total = widths.reduce((sum, w) => sum + w, 0);
      const normalizedWidths = widths.map((w) => (w / total) * 100);

      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        for (let c = 0; c < Math.min(colCount, row.cells.length); c++) {
          row.cells[c].style.width = `${normalizedWidths[c]}%`;
        }
      }

      handleContentInput();
    } catch (e) {
      console.warn("Failed to resize column:", e);
    }
  };

  // Regenerates line numbers gutter of pre/code blocks
  const syncAllCodeBlockGutters = (root: HTMLElement | null) => {
    if (!root) return;
    try {
      const preElements = root.querySelectorAll('.code-block-wrapper pre');
      preElements.forEach((pre) => {
        const text = (pre as HTMLElement).innerText;
        const lines = text.split('\n');
        const count = lines.length;
        const gutter = pre.previousElementSibling;
        if (gutter && gutter.classList.contains('code-block-gutter')) {
          gutter.innerHTML = Array.from({ length: count }, (_, i) => i + 1).join('<br>');
        }
      });
    } catch (e) {
      console.warn("Failed to sync code block gutters:", e);
    }
  };

  // Table active selections
  const [activeTableElement, setActiveTableElement] = useState<HTMLTableElement | null>(null);
  const [activeCellElement, setActiveCellElement] = useState<HTMLTableCellElement | null>(null);

  // Link Insertion Dialogs
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkOpenInNewTab, setLinkOpenInNewTab] = useState(true);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkError, setLinkError] = useState('');

  // Link and Embed States
  const [showLinkDropdown, setShowLinkDropdown] = useState(false);
  const [isNoteLinkModalOpen, setIsNoteLinkModalOpen] = useState(false);
  const [noteLinkSearchQuery, setNoteLinkSearchQuery] = useState('');
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedError, setEmbedError] = useState('');

  // Hover Preview Tooltip state
  const [hoveredNotePreview, setHoveredNotePreview] = useState<{
    x: number;
    y: number;
    title: string;
    summary: string;
  } | null>(null);

  // Note Backlinks state
  interface BacklinkItem {
    id: string;
    title: string;
  }
  const [backlinks, setBacklinks] = useState<BacklinkItem[]>([]);

  // Popover visual triggers
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [hoverTableRows, setHoverTableRows] = useState(3);
  const [hoverTableCols, setHoverTableCols] = useState(3);
  const [customTableRows, setCustomTableRows] = useState(3);
  const [customTableCols, setCustomTableCols] = useState(3);
  const [textColor, setTextColor] = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('transparent');

  // Enhanced Color Picker states
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [recentTextColors, setRecentTextColors] = useState<string[]>([]);
  const [recentHighlightColors, setRecentHighlightColors] = useState<string[]>([]);
  const [customHexInput, setCustomHexInput] = useState('');

  // Heading Dropdown state
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [currentHeading, setCurrentHeading] = useState<string>('p');

  // Font Size Dropdown state
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);

  // Preset color palettes
  const TEXT_COLOR_PRESETS = [
    '#000000', '#374151', '#6b7280', '#9ca3af',
    '#dc2626', '#ea580c', '#d97706', '#ca8a04',
    '#16a34a', '#059669', '#0d9488', '#0891b2',
    '#2563eb', '#4f46e5', '#7c3aed', '#9333ea',
    '#c026d3', '#db2777', '#e11d48', '#f43f5e'
  ];

  const HIGHLIGHT_COLOR_PRESETS = [
    'transparent', '#fef08a', '#fde68a', '#fed7aa',
    '#bbf7d0', '#a7f3d0', '#99f6e4', '#a5f3fc',
    '#bfdbfe', '#c7d2fe', '#ddd6fe', '#e9d5ff',
    '#fbcfe8', '#fecdd3', '#fca5a5', '#fdba74'
  ];

  const FONT_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64];

  const HEADING_OPTIONS: { value: string; label: string; tag: string }[] = [
    { value: 'p', label: 'Normal Text', tag: '<p>' },
    { value: 'h1', label: 'Heading 1', tag: '<h1>' },
    { value: 'h2', label: 'Heading 2', tag: '<h2>' },
    { value: 'h3', label: 'Heading 3', tag: '<h3>' },
    { value: 'h4', label: 'Heading 4', tag: '<h4>' },
    { value: 'h5', label: 'Heading 5', tag: '<h5>' },
    { value: 'h6', label: 'Heading 6', tag: '<h6>' },
  ];

  const toggleMenu = (menu: 'insert' | 'align' | 'table' | 'more' | 'textColor' | 'highlightColor' | 'heading' | 'fontSize' | 'linkDropdown') => {
    setShowInsertMenu(menu === 'insert' ? !showInsertMenu : false);
    setShowAlignMenu(menu === 'align' ? !showAlignMenu : false);
    setShowTableMenu(menu === 'table' ? !showTableMenu : false);
    setShowMoreActions(menu === 'more' ? !showMoreActions : false);
    setShowTextColorPicker(menu === 'textColor' ? !showTextColorPicker : false);
    setShowHighlightPicker(menu === 'highlightColor' ? !showHighlightPicker : false);
    setShowHeadingDropdown(menu === 'heading' ? !showHeadingDropdown : false);
    setShowFontSizeDropdown(menu === 'fontSize' ? !showFontSizeDropdown : false);
    setShowLinkDropdown(menu === 'linkDropdown' ? !showLinkDropdown : false);
  };

  const exportAsHTML = () => {
    if (!editorRef.current) return;
    const bodyText = editorRef.current.innerHTML;
    const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title || 'Untitled Note'}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; color: #1e293b; line-height: 1.6; }
    h1 { font-size: 2em; margin-bottom: 0.5em; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    table, th, td { border: 1px solid rgba(0,0,0,0.1); }
    th, td { padding: 10px; text-align: left; }
    blockquote { border-left: 4px solid #6366f1; padding-left: 16px; margin: 20px 0; font-style: italic; color: #4b5563; }
    pre { background: #1e1e2e; color: #cdd6f4; padding: 16px; border-radius: 8px; font-family: monospace; overflow-x: auto; }
  </style>
</head>
<body>
  <h1>${title || 'Untitled Note'}</h1>
  <div>${bodyText}</div>
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'untitled').toLowerCase().replace(/\s+/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    logActivity(`Exported note document as HTML to storage.`);
  };

  const exportAsMarkdown = () => {
    if (!editorRef.current) return;
    let md = `# ${title || 'Untitled Note'}\n\n`;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorRef.current.innerHTML;
    const children = Array.from(tempDiv.childNodes);
    children.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        const text = el.innerText.trim();
        if (!text && tag !== 'hr' && tag !== 'br') return;
        if (tag === 'h1') md += `# ${text}\n\n`;
        else if (tag === 'h2') md += `## ${text}\n\n`;
        else if (tag === 'h3') md += `### ${text}\n\n`;
        else if (tag === 'blockquote') md += `> ${text}\n\n`;
        else if (tag === 'pre') {
          const codeEl = el.querySelector('code');
          const code = codeEl?.innerText || el.innerText;
          const lang = el.getAttribute('data-lang') || 'javascript';
          md += `\`\`\`${lang}\n${code}\n\`\`\`\n\n`;
        } else if (tag === 'hr') md += `---\n\n`;
        else if (tag === 'ul') {
          const items = Array.from(el.querySelectorAll('li'));
          items.forEach(li => { md += `* ${li.innerText}\n`; });
          md += '\n';
        } else if (tag === 'ol') {
          const items = Array.from(el.querySelectorAll('li'));
          items.forEach((li, idx) => { md += `${idx + 1}. ${li.innerText}\n`; });
          md += '\n';
        } else {
          let paragraphText = el.innerHTML
            .replace(/<b>(.*?)<\/b>/gi, '**$1**')
            .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
            .replace(/<i>(.*?)<\/i>/gi, '*$1*')
            .replace(/<em>(.*?)<\/em>/gi, '*$1*')
            .replace(/<a href="(.*?)"(.*?)>(.*?)<\/a>/gi, '[$3]($1)');
          const cleanEl = document.createElement('div');
          cleanEl.innerHTML = paragraphText;
          md += `${cleanEl.innerText}\n\n`;
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) md += `${text}\n\n`;
      }
    });
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'untitled').toLowerCase().replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    logActivity(`Exported note document as Markdown (.MD).`);
  };

  const handlePrintDocument = () => {
    window.print();
    logActivity(`Triggered custom print service for Note card sheet.`);
  };

  // Session elapsed timer ticker
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Listen for document selection changes to instantly update toolbar button active states in real time
  useEffect(() => {
    const handleGlobalSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editorRef.current) {
        const range = selection.getRangeAt(0);
        // Only run synchronization if selection is inside this active editor area
        if (editorRef.current.contains(range.startContainer)) {
          updateActiveStates();
          updateActiveTableSelection();
          savedRangeRef.current = range;
        }
      }
    };
    document.addEventListener("selectionchange", handleGlobalSelection);
    return () => {
      document.removeEventListener("selectionchange", handleGlobalSelection);
    };
  }, []);

  // Listen for clicks outside the active dropdown menus and toolbar to auto-dismiss them
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.editor-toolbar-pill') || target.closest('.toolbar-dropdown')) {
        return;
      }
      closeAllToolbarDropdowns();
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  // Keyboard navigation & controls inside the Table size menu popover
  useEffect(() => {
    if (!showTableMenu) return;

    const handleTableMenuKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowTableMenu(false);
        if (editorRef.current) {
          editorRef.current.focus();
          restoreSelection();
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setHoverTableCols(prev => Math.min(TABLE_PICKER_COLS, prev + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setHoverTableCols(prev => Math.max(1, prev - 1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHoverTableRows(prev => Math.min(TABLE_PICKER_ROWS, prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHoverTableRows(prev => Math.max(1, prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleInsertTable(hoverTableCols, hoverTableRows);
        setShowTableMenu(false);
      }
    };

    document.addEventListener('keydown', handleTableMenuKeyDown);
    return () => {
      document.removeEventListener('keydown', handleTableMenuKeyDown);
    };
  }, [showTableMenu, hoverTableCols, hoverTableRows]);

  // Sync state if card changes externally
  useEffect(() => {
    if (card.id) {
      setTitle(card.title || '');
      setTags(card.tags || []);
      setIsLocked(card.isLocked || false);
      setPinCode(card.pinCode || '');
      setReminderTime(card.reminderTime ? new Date(card.reminderTime).toISOString().substring(0, 16) : '');
      
      const rawContent = card.contentHtml || card.textContent || '';
      const syncedHtml = syncInternalNoteLinks(rawContent);
      setTextContent(syncedHtml);
      setChecklist(card.checklistContent || []);
      setSketchDataUrl(card.sketchDataUrl || '');
      setAudioUrl(card.audioUrl || '');
      setAudioDuration(card.audioDuration || 0);
      setPhotoUrl(card.photoUrl || '');
      initialContentRef.current = syncedHtml;
      historyStackRef.current = [syncedHtml];
      historyIndexRef.current = 0;
      setCanUndoState(false);
      setCanRedoState(false);
      if (editorRef.current) {
        editorRef.current.innerHTML = syncedHtml;
        
        // Sync code gutters
        setTimeout(() => {
          if (editorRef.current) {
            syncAllCodeBlockGutters(editorRef.current);
          }
        }, 100);
      }
      
      updateBacklinks();

      // Initialize snapshot records
      const savedSnaps = localStorage.getItem(`note_snapshots_catalog_${card.id}`);
      if (savedSnaps) {
        setVersionSnapshots(JSON.parse(savedSnaps));
      } else {
        const firstSnap: NoteSnapshot = {
          id: `snap-${Date.now()}`,
          timestamp: Date.now(),
          title: card.title || 'Untitled Draft',
          textContent: syncedHtml,
          checklist: card.checklistContent || []
        };
        setVersionSnapshots([firstSnap]);
        localStorage.setItem(`note_snapshots_catalog_${card.id}`, JSON.stringify([firstSnap]));
      }

      // Log event
      setActivityLogs([
        { timestamp: Date.now(), text: `Loaded note: "${card.title || 'Untitled'}" into desk editor.` }
      ]);
    }
  }, [card.id]);

  // Sync word and character counts dynamically
  useEffect(() => {
    let cleanText = '';
    if (card.type === 'text') {
      cleanText = editorRef.current?.innerText || textContent || '';
    } else if (card.type === 'checklist') {
      cleanText = checklist.map(t => t.text).join(' ');
    }
    const words = cleanText.trim().split(/\s+/).filter(w => w.length > 0).length;
    setWordCount(words);
    setCharCount(cleanText.length);
  }, [textContent, checklist]);

  // Master persistent save updater with nice status animations
  const triggerPushUpdate = (overrides: Partial<NoteCard> = {}) => {
    setSaveStatus('saving');

    const nextReminder = overrides.reminderTime !== undefined 
      ? overrides.reminderTime 
      : (reminderTime ? new Date(reminderTime).getTime() : undefined);

    const updatedCard: Partial<NoteCard> = {
      ...card,
      title: overrides.title !== undefined ? overrides.title : title,
      tags: overrides.tags !== undefined ? overrides.tags : tags,
      isLocked: overrides.isLocked !== undefined ? overrides.isLocked : isLocked,
      pinCode: overrides.pinCode !== undefined ? overrides.pinCode : (isLocked ? pinCode : undefined),
      reminderTime: nextReminder,
      updatedAt: Date.now(),
      deskThemeIdx: overrides.deskThemeIdx !== undefined ? overrides.deskThemeIdx : deskThemeIdx,
      boardColor: overrides.boardColor !== undefined ? overrides.boardColor : card.boardColor,
      
      // Card specific variables
      textContent: card.type === 'text' ? (overrides.textContent !== undefined ? overrides.textContent : textContent) : undefined,
      contentHtml: card.type === 'text' && card.contentHtml !== undefined ? (overrides.textContent !== undefined ? overrides.textContent : textContent) : undefined,
      checklistContent: card.type === 'checklist' ? (overrides.checklistContent !== undefined ? overrides.checklistContent : checklist) : undefined,
      sketchDataUrl: card.type === 'sketch' ? (overrides.sketchDataUrl !== undefined ? overrides.sketchDataUrl : sketchDataUrl) : undefined,
      audioUrl: card.type === 'audio' ? (overrides.audioUrl !== undefined ? overrides.audioUrl : audioUrl) : undefined,
      audioDuration: card.type === 'audio' ? (overrides.audioDuration !== undefined ? overrides.audioDuration : audioDuration) : undefined,
      photoUrl: card.type === 'photo' ? (overrides.photoUrl !== undefined ? overrides.photoUrl : photoUrl) : undefined,
    };

    onSave(updatedCard);
    
    setTimeout(() => {
      setSaveStatus('saved');
    }, 450);

    // Save automatic snapshot for version control if content changed
    if (card.id && (overrides.textContent || overrides.checklistContent || overrides.title)) {
      setVersionSnapshots(prev => {
        // Prevent duplicate snapshots of exact same content
        if (prev.length > 0 && prev[0].textContent === textContent && prev[0].title === title) {
          return prev;
        }
        const newSnap: NoteSnapshot = {
          id: `snap-${Date.now()}`,
          timestamp: Date.now(),
          title: overrides.title || title,
          textContent: overrides.textContent || textContent,
          checklist: overrides.checklistContent || checklist
        };
        const updated = [newSnap, ...prev.slice(0, 14)]; // Limit to 15 versions
        localStorage.setItem(`note_snapshots_catalog_${card.id}`, JSON.stringify(updated));
        return updated;
      });
    }
  };

  const handleTitleChange = (nextTitle: string) => {
    setTitle(nextTitle);
    triggerPushUpdate({ title: nextTitle });
  };

  // Keyboard/Writing events to update state
  // Push active HTML snapshot to undo history stacks
  const updateHistoryStateTracking = () => {
    setCanUndoState(historyIndexRef.current > 0);
    setCanRedoState(historyIndexRef.current < historyStackRef.current.length - 1);
  };

  const pushHistoryState = (newHtml: string) => {
    const stack = historyStackRef.current;
    const index = historyIndexRef.current;
    
    if (stack[index] === newHtml) return;

    // Prune the stack forward if we undo-ed and typed new content
    const prunedStack = stack.slice(0, index + 1);
    prunedStack.push(newHtml);
    historyStackRef.current = prunedStack;
    historyIndexRef.current = prunedStack.length - 1;
    updateHistoryStateTracking();
  };

  const handleEditorInputDebounced = (html: string) => {
    if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
    historyTimeoutRef.current = setTimeout(() => {
      pushHistoryState(html);
    }, 800);
  };

  const handleCustomUndo = () => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      const prevHtml = historyStackRef.current[historyIndexRef.current];
      if (editorRef.current) {
        editorRef.current.innerHTML = prevHtml;
        setTextContent(prevHtml);
        triggerPushUpdate({ textContent: prevHtml });
      }
      logActivity('Undone last document style action.');
      updateHistoryStateTracking();
      updateActiveStates();
    }
  };

  const handleCustomRedo = () => {
    if (historyIndexRef.current < historyStackRef.current.length - 1) {
      historyIndexRef.current++;
      const nextHtml = historyStackRef.current[historyIndexRef.current];
      if (editorRef.current) {
        editorRef.current.innerHTML = nextHtml;
        setTextContent(nextHtml);
        triggerPushUpdate({ textContent: nextHtml });
      }
      logActivity('Redone last document style action.');
      updateHistoryStateTracking();
      updateActiveStates();
    }
  };

  const handleContentInput = () => {
    if (editorRef.current) {
      const nextContent = editorRef.current.innerHTML;
      setTextContent(nextContent);
      triggerPushUpdate({ textContent: nextContent });
      handleEditorInputDebounced(nextContent);
      // Ensure the active styling highlighted buttons stay matches on input
      updateActiveStates();
    }
  };

  const handleContentBlur = () => {
    if (editorRef.current) {
      const nextContent = editorRef.current.innerHTML;
      setTextContent(nextContent);
      triggerPushUpdate({ textContent: nextContent });
      pushHistoryState(nextContent);
    }
  };

  const handleEditorMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    try {
      const target = e.target as HTMLElement;
      const linkEl = target.closest('a[data-note-link="true"]');
      if (linkEl) {
        const noteId = linkEl.getAttribute('data-note-id');
        if (noteId) {
          const catalog = getNotesCatalog();
          const targetNote = catalog.find(n => n.id === noteId);
          if (targetNote) {
            const rect = linkEl.getBoundingClientRect();
            const containerRect = editorRef.current?.getBoundingClientRect();
            const x = rect.left - (containerRect?.left || 0);
            const y = rect.bottom - (containerRect?.top || 0) + 5;
            
            const rawText = targetNote.textContent || '';
            const unescaped = rawText
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'");
            const cleanText = unescaped.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
            
            setHoveredNotePreview({
              x,
              y,
              title: targetNote.title || 'Untitled Note',
              summary: cleanText.substring(0, 150) + (cleanText.length > 150 ? '...' : '') || 'Empty note'
            });
            return;
          }
        }
      }
      setHoveredNotePreview(null);
    } catch (err) {
      console.warn("Failed to handle hover preview:", err);
    }
  };

  const handleEditorMouseLeave = () => {
    setHoveredNotePreview(null);
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const isMac = navigator.userAgent.toLowerCase().includes('mac');
    const isCtrlCmd = isMac ? e.metaKey : e.ctrlKey;

    // Bold (Ctrl/Cmd + B)
    if (isCtrlCmd && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      formatCommand('bold');
      return;
    }

    // Italic (Ctrl/Cmd + I)
    if (isCtrlCmd && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      formatCommand('italic');
      return;
    }

    // Underline (Ctrl/Cmd + U)
    if (isCtrlCmd && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      formatCommand('underline');
      return;
    }

    // Link (Ctrl/Cmd + K)
    if (isCtrlCmd && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      toggleMenu('linkDropdown');
      return;
    }

    // Undo (Ctrl/Cmd + Z)
    if (isCtrlCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      handleCustomUndo();
      return;
    }

    // Redo (Ctrl/Cmd + Y or Ctrl/Cmd + Shift + Z)
    if (isCtrlCmd && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
      e.preventDefault();
      handleCustomRedo();
      return;
    }

    // Strikethrough (Alt + Shift + 5)
    if (e.altKey && e.shiftKey && e.key === '5') {
      e.preventDefault();
      formatCommand('strikeThrough');
      return;
    }

    // Enter Key Handler for lists (Double Enter exits list)
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let node: Node | null = range.startContainer;
        let liNode: HTMLElement | null = null;
        while (node && node !== editorRef.current) {
          if (node.nodeName === 'LI') {
            liNode = node as HTMLElement;
            break;
          }
          node = node.parentNode;
        }

        if (liNode) {
          const text = liNode.textContent || '';
          const cleanText = text.replace(/[\u200B\s]/g, '').trim();
          if (cleanText === '') {
            e.preventDefault();
            const parentList = liNode.parentNode as HTMLElement;
            if (parentList) {
              const isOrdered = parentList.nodeName === 'OL';
              document.execCommand(isOrdered ? 'insertOrderedList' : 'insertUnorderedList');
            }
            return;
          }
        }
      }
    }

    // Tab / Shift+Tab Handler for lists and tables
    if (e.key === 'Tab') {
      e.preventDefault();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let node: Node | null = range.startContainer;
        
        let liNode: HTMLElement | null = null;
        let cellNode: HTMLTableCellElement | null = null;

        while (node && node !== editorRef.current) {
          if (node.nodeName === 'LI') {
            liNode = node as HTMLElement;
          }
          if (node.nodeName === 'TD' || node.nodeName === 'TH') {
            cellNode = node as HTMLTableCellElement;
          }
          node = node.parentNode;
        }

        if (liNode) {
          if (e.shiftKey) {
            document.execCommand('outdent');
          } else {
            document.execCommand('indent');
          }
        } else if (cellNode) {
          const row = cellNode.parentElement as HTMLTableRowElement;
          const table = row?.parentElement?.parentElement as HTMLTableElement;
          if (row && table) {
            const isLastRow = row.rowIndex === table.rows.length - 1;
            const isLastCell = cellNode.cellIndex === row.cells.length - 1;

            if (isLastRow && isLastCell && !e.shiftKey) {
              tableInsertRow(true);
              const newRow = row.nextElementSibling as HTMLTableRowElement;
              if (newRow && newRow.cells.length > 0) {
                const firstCell = newRow.cells[0];
                firstCell.focus();
                const newRange = document.createRange();
                newRange.selectNodeContents(firstCell);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
              }
            } else {
              let nextCell: HTMLTableCellElement | null = null;
              if (e.shiftKey) {
                if (cellNode.cellIndex > 0) {
                  nextCell = row.cells[cellNode.cellIndex - 1];
                } else if (row.rowIndex > 0) {
                  const prevRow = table.rows[row.rowIndex - 1];
                  nextCell = prevRow.cells[prevRow.cells.length - 1];
                }
              } else {
                if (cellNode.cellIndex < row.cells.length - 1) {
                  nextCell = row.cells[cellNode.cellIndex + 1];
                } else if (row.rowIndex < table.rows.length - 1) {
                  const nextRow = table.rows[row.rowIndex + 1];
                  nextCell = nextRow.cells[0];
                }
              }

              if (nextCell) {
                nextCell.focus();
                const newRange = document.createRange();
                newRange.selectNodeContents(nextCell);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
              }
            }
          }
        } else {
          document.execCommand('insertText', false, '\t');
        }
      }
      return;
    }
  };

  const openLinkModal = () => {
    ensureSelectionInEditor();
    setIsEditingLink(false);
    setLinkError('');
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        const wordRange = getWordRangeAtCursor();
        if (wordRange) {
          selection.removeAllRanges();
          selection.addRange(wordRange);
        }
      }
    }

    const selection2 = window.getSelection();
    if (selection2) {
      const text = selection2.toString();
      setLinkLabel(text || '');
      // Try to find if selection is inside an anchor element
      let node = selection2.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'A') {
          const anchor = node as HTMLAnchorElement;
          setLinkUrl(anchor.getAttribute('href') || anchor.href);
          setLinkLabel(anchor.innerText);
          setLinkOpenInNewTab(anchor.target === '_blank');
          setIsEditingLink(true);
          break;
        }
        node = node.parentNode;
      }
    }
    
    saveSelection();
    setIsLinkModalOpen(true);
  };

  const handleConfirmInsertLink = () => {
    if (!linkUrl.trim()) {
      setLinkError('URL address is required');
      return;
    }

    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i;
    const isRelative = /^[/#]/.test(linkUrl.trim());
    const isValidUrl = isRelative || urlPattern.test(linkUrl.trim());

    if (!isValidUrl) {
      setLinkError('Please enter a valid URL address');
      return;
    }

    setLinkError('');
    restoreSelection();
    let formattedUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl) && !/^\//.test(formattedUrl)) {
      formattedUrl = 'https://' + formattedUrl;
    }
    const displayLabel = linkLabel.trim() || formattedUrl;
    
    // If we were editing, remove old link first
    if (isEditingLink) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let node = sel.anchorNode;
        let anchorElement: HTMLAnchorElement | null = null;
        while (node && node !== editorRef.current) {
          if (node.nodeName === 'A') {
            anchorElement = node as HTMLAnchorElement;
            break;
          }
          node = node.parentNode;
        }
        if (anchorElement) {
          const parent = anchorElement.parentNode;
          if (parent) {
            while (anchorElement.firstChild) {
              parent.insertBefore(anchorElement.firstChild, anchorElement);
            }
            parent.removeChild(anchorElement);
          }
        }
      }
    }

    const linkHtml = `<a href="${formattedUrl}" target="${linkOpenInNewTab ? '_blank' : '_self'}" class="text-sky-500 hover:text-sky-600 hover:underline inline-flex items-center gap-0.5 font-semibold cursor-pointer" rel="noopener noreferrer">${displayLabel}</a>`;
    insertHtmlAtCursor(linkHtml);
    setIsLinkModalOpen(false);
    setLinkUrl('');
    setLinkLabel('');
    setIsEditingLink(false);
    savedRangeRef.current = null;
    logActivity(`Inserted inline hyperlink element for "${displayLabel}".`);
  };

  const handleRemoveLink = () => {
    restoreSelection();
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      let node = selection.anchorNode;
      let anchorElement: HTMLAnchorElement | null = null;
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'A') {
          anchorElement = node as HTMLAnchorElement;
          break;
        }
        node = node.parentNode;
      }
      if (anchorElement) {
        const parent = anchorElement.parentNode;
        if (parent) {
          while (anchorElement.firstChild) {
            parent.insertBefore(anchorElement.firstChild, anchorElement);
          }
          parent.removeChild(anchorElement);
        }
      } else {
        document.execCommand('unlink');
      }
    } else {
      document.execCommand('unlink');
    }
    setIsLinkModalOpen(false);
    setLinkUrl('');
    setLinkLabel('');
    setIsEditingLink(false);
    savedRangeRef.current = null;
    handleContentInput();
    if (editorRef.current) {
      pushHistoryState(editorRef.current.innerHTML);
    }
    logActivity('Removed inline hyperlink.');
  };

  const rgbToHex = (rgb: string): string => {
    if (!rgb) return '';
    if (rgb.startsWith('#')) return rgb;
    const match = rgb.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (match) {
      return "#" + ("0" + parseInt(match[1], 10).toString(16)).slice(-2) +
                   ("0" + parseInt(match[2], 10).toString(16)).slice(-2) +
                   ("0" + parseInt(match[3], 10).toString(16)).slice(-2);
    }
    return rgb;
  };

  const updateActiveStates = () => {
    try {
      let hasCode = false;
      const selection = window.getSelection();
      let selectedText = "";
      if (selection && selection.rangeCount > 0) {
        selectedText = selection.toString();
        let node: Node | null = selection.getRangeAt(0).startContainer;
        if (editorRef.current && editorRef.current.contains(node)) {
          let codeNode = node;
          while (codeNode && codeNode !== editorRef.current) {
            if (codeNode.nodeName === 'CODE') {
              hasCode = true;
              break;
            }
            codeNode = codeNode.parentNode!;
          }

          let styleNode: Node | null = node;
          if (styleNode.nodeType === Node.TEXT_NODE) {
            styleNode = styleNode.parentNode;
          }
          let foundColor = '';
          let foundBg = '';
          let foundSize = '';
          while (styleNode && styleNode !== editorRef.current) {
            if (styleNode.nodeType === Node.ELEMENT_NODE) {
              const el = styleNode as HTMLElement;
              if (!foundColor && el.style.color) {
                foundColor = rgbToHex(el.style.color);
              }
              if (!foundBg && el.style.backgroundColor) {
                foundBg = rgbToHex(el.style.backgroundColor);
              }
              if (!foundSize && el.style.fontSize) {
                foundSize = el.style.fontSize;
              }
              if (!foundColor && el.tagName === 'FONT') {
                const fontCol = el.getAttribute('color');
                if (fontCol) foundColor = fontCol;
              }
            }
            styleNode = styleNode.parentNode;
          }

          if (foundColor) setTextColor(foundColor);
          else setTextColor('#000000');

          if (foundBg) setHighlightColor(foundBg);
          else setHighlightColor('transparent');

          if (foundSize) {
            const parsedSize = parseInt(foundSize, 10);
            if (!isNaN(parsedSize)) {
              setFontSize(parsedSize);
            }
          } else {
            setFontSize(16);
          }
        }
      }

      const states = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikethrough: document.queryCommandState('strikeThrough') || document.queryCommandState('strikethrough'),
        alignLeft: document.queryCommandState('justifyLeft') || (!document.queryCommandState('justifyCenter') && !document.queryCommandState('justifyRight') && !document.queryCommandState('justifyFull')),
        alignCenter: document.queryCommandState('justifyCenter'),
        alignRight: document.queryCommandState('justifyRight'),
        alignJustify: document.queryCommandState('justifyFull'),
        bulletList: document.queryCommandState('insertUnorderedList'),
        orderedList: document.queryCommandState('insertOrderedList'),
        code: hasCode,
      };

      setActiveStyles(states);
      console.log(`[Rich Editor Diagnostics] Toolbar State Updated. Live active states:`, states, `Selection length: ${selectedText.length}`);
    } catch (e) {
      console.error("[Rich Editor Diagnostics] Failed to process active state styles: ", e);
    }
  };

  const updateActiveTableSelection = () => {
    try {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let node: Node | null = range.startContainer;
        
        // Robust check for ELEMENT_NODE selection target (like TR or TBODY inside empty cells)
        if (node.nodeType === Node.ELEMENT_NODE) {
          const offset = range.startOffset;
          if (node.childNodes && node.childNodes.length > 0) {
            const child = node.childNodes[Math.min(offset, node.childNodes.length - 1)];
            if (child) {
              node = child;
            }
          }
        }

        if (editorRef.current && editorRef.current.contains(node)) {
          let cell: HTMLTableCellElement | null = null;
          let table: HTMLTableElement | null = null;
          
          let temp: Node | null = node;
          while (temp && temp !== editorRef.current) {
            if (temp.nodeName === 'TD' || temp.nodeName === 'TH') {
              cell = temp as HTMLTableCellElement;
            }
            if (temp.nodeName === 'TABLE') {
              table = temp as HTMLTableElement;
              break;
            }
            temp = temp.parentNode;
          }

          if (cell && table) {
            setActiveCellElement(cell);
            setActiveTableElement(table);
            return;
          }
        }
      }
      setActiveCellElement(null);
      setActiveTableElement(null);
    } catch (e) {
      console.warn("Failed to update active table selection: ", e);
    }
  };

  const formatCommand = (
    cmd: string, 
    val: string = '', 
    customStyleProps?: Record<string, string>
  ) => {
    try {
      console.log(`[Rich Editor Diagnostics] formatCommand: "${cmd}" with value: "${val}"`);
      ensureSelectionInEditor();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);

      if (range.collapsed) {
        const wordRange = getWordRangeAtCursor();
        if (wordRange) {
          const originalContainer = range.startContainer;
          const originalOffset = range.startOffset;

          const wordStartContainer = wordRange.startContainer;
          const wordStartOffset = wordRange.startOffset;
          const relativeOffset = originalOffset - wordStartOffset;

          selection.removeAllRanges();
          selection.addRange(wordRange);

          if (customStyleProps) {
            if (cmd === 'fontSize') {
              document.execCommand('fontSize', false, '7');
              if (editorRef.current) {
                const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
                fontElements.forEach((el) => {
                  const span = document.createElement('span');
                  Object.assign(span.style, customStyleProps);
                  span.innerHTML = el.innerHTML;
                  el.parentNode?.replaceChild(span, el);
                });
              }
            } else if (cmd === 'foreColor') {
              document.execCommand('foreColor', false, val);
              if (editorRef.current) {
                const fontElements = editorRef.current.querySelectorAll('font[color]');
                fontElements.forEach((el) => {
                  const fontCol = el.getAttribute('color');
                  const span = document.createElement('span');
                  span.style.color = fontCol || val;
                  span.innerHTML = el.innerHTML;
                  el.parentNode?.replaceChild(span, el);
                });
              }
            } else if (cmd === 'backColor') {
              document.execCommand('backColor', false, val);
              if (editorRef.current) {
                const fontElements = editorRef.current.querySelectorAll('font[style*="background-color"]');
                fontElements.forEach((el) => {
                  const span = document.createElement('span');
                  span.style.backgroundColor = el.style.backgroundColor || val;
                  span.innerHTML = el.innerHTML;
                  el.parentNode?.replaceChild(span, el);
                });
              }
            }
          } else {
            document.execCommand(cmd, false, val);
          }

          const newRange = selection.getRangeAt(0);
          const container = newRange.startContainer;
          let targetTextNode: Node | null = null;
          if (container.nodeType === Node.TEXT_NODE) {
            targetTextNode = container;
          } else {
            targetTextNode = findFirstTextNode(container);
          }

          if (targetTextNode) {
            const restoreRange = document.createRange();
            const targetOffset = Math.min(relativeOffset, targetTextNode.textContent?.length || 0);
            restoreRange.setStart(targetTextNode, targetOffset);
            restoreRange.setEnd(targetTextNode, targetOffset);
            selection.removeAllRanges();
            selection.addRange(restoreRange);
          }
        } else {
          if (customStyleProps) {
            const span = document.createElement('span');
            Object.assign(span.style, customStyleProps);
            span.appendChild(document.createTextNode('\u200B'));
            
            range.insertNode(span);
            
            const newRange = document.createRange();
            newRange.setStart(span.firstChild!, 1);
            newRange.setEnd(span.firstChild!, 1);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } else {
            document.execCommand(cmd, false, val);
          }
        }
      } else {
        if (customStyleProps) {
          if (cmd === 'fontSize') {
            document.execCommand('fontSize', false, '7');
            if (editorRef.current) {
              const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
              fontElements.forEach((el) => {
                const span = document.createElement('span');
                Object.assign(span.style, customStyleProps);
                span.innerHTML = el.innerHTML;
                el.parentNode?.replaceChild(span, el);
              });
            }
          } else if (cmd === 'foreColor') {
            document.execCommand('foreColor', false, val);
            if (editorRef.current) {
              const fontElements = editorRef.current.querySelectorAll('font[color]');
              fontElements.forEach((el) => {
                const fontCol = el.getAttribute('color');
                const span = document.createElement('span');
                span.style.color = fontCol || val;
                span.innerHTML = el.innerHTML;
                el.parentNode?.replaceChild(span, el);
              });
            }
          } else if (cmd === 'backColor') {
            document.execCommand('backColor', false, val);
            if (editorRef.current) {
              const fontElements = editorRef.current.querySelectorAll('font[style*="background-color"]');
              fontElements.forEach((el) => {
                const span = document.createElement('span');
                span.style.backgroundColor = el.style.backgroundColor || val;
                span.innerHTML = el.innerHTML;
                el.parentNode?.replaceChild(span, el);
              });
            }
          }
        } else {
          document.execCommand(cmd, false, val);
        }
      }

      handleContentInput();
      updateActiveStates();
      if (editorRef.current) {
        pushHistoryState(editorRef.current.innerHTML);
      }
    } catch (e) {
      console.error(`[Rich Editor Diagnostics] execCommand formatting failed! Error cause: `, e);
    }
  };

  const insertHtmlAtCursor = (html: string) => {
    try {
      if (savedRangeRef.current && editorRef.current && (!window.getSelection() || !window.getSelection()?.rangeCount || !editorRef.current.contains(window.getSelection()!.getRangeAt(0).startContainer))) {
        restoreSelection();
      }
      editorRef.current?.focus();
      const selection = window.getSelection();
      const isSelectionInEditor = selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.getRangeAt(0).startContainer);

      if (isSelectionInEditor && selection) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const frag = document.createDocumentFragment();
        let node;
        let lastNode;
        while ((node = tempDiv.firstChild)) {
          lastNode = frag.appendChild(node);
        }
        range.insertNode(frag);
        
        if (lastNode) {
          const nextRange = range.cloneRange();
          nextRange.setStartAfter(lastNode);
          nextRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(nextRange);
        }
      } else if (editorRef.current) {
        // Safe fallback - select the end of editor contents and insert
        editorRef.current.focus();
        const fallbackSelection = window.getSelection();
        if (fallbackSelection) {
          const newRange = document.createRange();
          newRange.selectNodeContents(editorRef.current);
          newRange.collapse(false); // collapse to end
          fallbackSelection.removeAllRanges();
          fallbackSelection.addRange(newRange);
          
          const range = fallbackSelection.getRangeAt(0);
          range.deleteContents();
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;
          const frag = document.createDocumentFragment();
          let node;
          let lastNode;
          while ((node = tempDiv.firstChild)) {
            lastNode = frag.appendChild(node);
          }
          range.insertNode(frag);
          if (lastNode) {
            const nextRange = range.cloneRange();
            nextRange.setStartAfter(lastNode);
            nextRange.collapse(true);
            fallbackSelection.removeAllRanges();
            fallbackSelection.addRange(nextRange);
          }
        }
      }
      handleContentInput();
      updateActiveStates();
      if (editorRef.current) {
        pushHistoryState(editorRef.current.innerHTML);
      }
    } catch (e) {
      console.warn("insertHtmlAtCursor failed: ", e);
    }
  };

  const handleInsertTable = (cols = 3, rows = 3) => {
    const tempId = `table-temp-${Date.now()}`;
    let html = `<table id="${tempId}" style="width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0;"><tbody>`;
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        html += `<td style="width: ${100 / cols}%;" contenteditable="true"><br></td>`;
      }
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    insertHtmlAtCursor(html);
    
    // Focus the first cell of the newly inserted table
    setTimeout(() => {
      if (editorRef.current) {
        const table = editorRef.current.querySelector(`#${tempId}`) as HTMLTableElement;
        if (table) {
          table.removeAttribute('id');
          const firstCell = table.querySelector('td') as HTMLTableCellElement;
          if (firstCell) {
            firstCell.focus();
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              range.selectNodeContents(firstCell);
              range.collapse(true); // Put cursor at the start of the cell
              selection.removeAllRanges();
              selection.addRange(range);
              
              // Sync states
              setActiveCellElement(firstCell);
              setActiveTableElement(table);
            }
          }
        }
      }
    }, 50);

    logActivity(`Inserted table with structure: ${cols} cols x ${rows} rows.`);
  };

  const handleTouchMoveNoteTable = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 0) return;
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element) {
      const btn = element.closest('[data-row]') as HTMLButtonElement | null;
      if (btn) {
        const rIdx = parseInt(btn.getAttribute('data-row') || '0', 10);
        const cIdx = parseInt(btn.getAttribute('data-col') || '0', 10);
        
        setHoverTableRows(rIdx + 1);
        setHoverTableCols(cIdx + 1);
      }
    }
  };

  const handleTouchEndNoteTable = (e: React.TouchEvent) => {
    e.preventDefault();
    handleInsertTable(hoverTableCols, hoverTableRows);
    setShowTableMenu(false);
  };

  const handleInsertLine = () => {
    insertHtmlAtCursor('<hr class="border-t border-dashed border-black/10 my-6" contenteditable="false" /><p><br></p>');
  };

  const redistributeTableColumnWidths = (table: HTMLTableElement) => {
    try {
      const rows = table.rows;
      if (rows.length === 0) return;
      let maxCols = 0;
      for (let i = 0; i < rows.length; i++) {
        maxCols = Math.max(maxCols, rows[i].cells.length);
      }
      if (maxCols === 0) return;
      
      const equalWidth = `${100 / maxCols}%`;
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        for (let c = 0; c < row.cells.length; c++) {
          row.cells[c].style.width = equalWidth;
        }
      }
    } catch (e) {
      console.warn("Failed to redistribute table columns: ", e);
    }
  };

  const setTableWidth = (width: string) => {
    if (!activeTableElement) return;
    activeTableElement.style.width = width;
    handleContentInput();
  };

  const setTableAlignment = (align: 'left' | 'center' | 'right') => {
    if (!activeTableElement) return;
    if (align === 'left') {
      activeTableElement.style.marginLeft = '0';
      activeTableElement.style.marginRight = 'auto';
    } else if (align === 'center') {
      activeTableElement.style.marginLeft = 'auto';
      activeTableElement.style.marginRight = 'auto';
    } else if (align === 'right') {
      activeTableElement.style.marginLeft = 'auto';
      activeTableElement.style.marginRight = '0';
    }
    handleContentInput();
  };

  const tableInsertRow = (below: boolean = true) => {
    if (!activeCellElement || !activeTableElement) return;
    const activeRow = activeCellElement.parentElement as HTMLTableRowElement;
    if (!activeRow) return;
    
    const cellCount = activeRow.cells.length;
    const newRow = document.createElement('tr');
    
    for (let i = 0; i < cellCount; i++) {
      const newCell = document.createElement('td');
      newCell.contentEditable = 'true';
      newCell.innerHTML = '<br>';
      // Inherit the column width directly from the active row cell to preserve resizing
      if (activeRow.cells[i]) {
        newCell.style.width = activeRow.cells[i].style.width;
      }
      newRow.appendChild(newCell);
    }
    
    if (below) {
      activeRow.insertAdjacentElement('afterend', newRow);
    } else {
      activeRow.insertAdjacentElement('beforebegin', newRow);
    }
    handleContentInput();
  };

  const tableDeleteRow = () => {
    if (!activeCellElement || !activeTableElement) return;
    const activeRow = activeCellElement.parentElement as HTMLTableRowElement;
    if (activeRow) {
      activeRow.remove();
      handleContentInput();
      setActiveCellElement(null);
      setActiveTableElement(null);
    }
  };

  const tableInsertColumn = (right: boolean = true) => {
    if (!activeCellElement || !activeTableElement) return;
    const cellIndex = activeCellElement.cellIndex;
    const rows = activeTableElement.rows;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const newCell = document.createElement('td');
      newCell.contentEditable = 'true';
      newCell.innerHTML = '<br>';
      if (right) {
        if (cellIndex + 1 < row.cells.length) {
          row.insertBefore(newCell, row.cells[cellIndex + 1]);
        } else {
          row.appendChild(newCell);
        }
      } else {
        row.insertBefore(newCell, row.cells[cellIndex]);
      }
    }
    redistributeTableColumnWidths(activeTableElement);
    handleContentInput();
  };

  const tableDeleteColumn = () => {
    if (!activeCellElement || !activeTableElement) return;
    const cellIndex = activeCellElement.cellIndex;
    const rows = activeTableElement.rows;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row && row.cells.length > cellIndex) {
        row.deleteCell(cellIndex);
      }
    }
    redistributeTableColumnWidths(activeTableElement);
    handleContentInput();
    setActiveCellElement(null);
    setActiveTableElement(null);
  };

  const tableMergeCells = () => {
    if (!activeCellElement || !activeTableElement) return;
    const activeRow = activeCellElement.parentElement as HTMLTableRowElement;
    const cellIndex = activeCellElement.cellIndex;
    if (activeRow && cellIndex < activeRow.cells.length - 1) {
      const rightCell = activeRow.cells[cellIndex + 1];
      activeCellElement.colSpan = (activeCellElement.colSpan || 1) + (rightCell.colSpan || 1);
      activeCellElement.innerHTML += '<br>' + rightCell.innerHTML;
      rightCell.remove();
      handleContentInput();
    }
  };

  const tableSplitCells = () => {
    if (!activeCellElement || !activeTableElement) return;
    if (activeCellElement.colSpan > 1) {
      const activeRow = activeCellElement.parentElement as HTMLTableRowElement;
      const cellIndex = activeCellElement.cellIndex;
      const originalColSpan = activeCellElement.colSpan;
      activeCellElement.colSpan = 1;
      
      for (let i = 1; i < originalColSpan; i++) {
        const newCell = document.createElement('td');
        newCell.contentEditable = 'true';
        newCell.innerHTML = '<br>';
        activeRow.insertBefore(newCell, activeRow.cells[cellIndex + 1]);
      }
      redistributeTableColumnWidths(activeTableElement);
      handleContentInput();
    }
  };

  const insertChecklistElement = () => {
    insertHtmlAtCursor('<div class="flex items-center gap-2.5 my-2" contenteditable="false"><input type="checkbox" class="w-4 h-4 rounded-md border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" onclick="this.checked ? this.nextSibling.classList.add(\'line-through\', \'text-slate-400\') : this.nextSibling.classList.remove(\'line-through\', \'text-slate-400\')" /><span class="outline-none text-[15px] text-slate-800 dark:text-slate-200" contenteditable="true">Checklist item...</span></div><p><br></p>');
  };

  const insertCodeBlockElement = (lang: string = 'javascript') => {
    const defaultCode = `// Write code here\nconsole.log("Modern Rich Editor successfully implemented!");`;
    const linesCount = defaultCode.split('\n').length;
    const gutterHtml = Array.from({ length: linesCount }, (_, i) => i + 1).join('<br>');
    
    const blockHtml = `
      <div class="code-block-wrapper my-4 border border-slate-800 rounded-xl overflow-hidden bg-slate-900 text-slate-100 font-mono text-xs relative" contenteditable="false">
        <div class="code-block-header flex items-center justify-between px-4 py-2 bg-slate-950 border-b border-slate-800 text-[11px] text-slate-400 select-none">
          <select class="bg-transparent border-none outline-none font-bold text-slate-350 cursor-pointer" onchange="this.closest('.code-block-wrapper').querySelector('pre').setAttribute('data-lang', this.value)">
            <option value="javascript" ${lang === 'javascript' ? 'selected' : ''}>JavaScript</option>
            <option value="typescript" ${lang === 'typescript' ? 'selected' : ''}>TypeScript</option>
            <option value="python" ${lang === 'python' ? 'selected' : ''}>Python</option>
            <option value="html" ${lang === 'html' ? 'selected' : ''}>HTML</option>
            <option value="css" ${lang === 'css' ? 'selected' : ''}>CSS</option>
          </select>
          <button class="flex items-center gap-1 hover:text-white transition-colors cursor-pointer font-bold" onclick="navigator.clipboard.writeText(this.closest('.code-block-wrapper').querySelector('pre code').innerText); this.textContent='Copied!'; setTimeout(() => this.textContent='Copy Code', 1500)">
            Copy Code
          </button>
        </div>
        <div class="flex items-stretch">
          <div class="code-block-gutter px-2 py-3 bg-slate-950/40 text-slate-500 text-right select-none border-r border-slate-800/60 min-w-[32px] leading-normal">
            ${gutterHtml}
          </div>
          <pre class="flex-1 p-3 m-0 leading-normal outline-none overflow-x-auto select-text" contenteditable="true" data-lang="${lang}" oninput="
            const text = this.innerText;
            const lines = text.split('\\n');
            const count = lines.length;
            const gutter = this.previousElementSibling;
            gutter.innerHTML = Array.from({length: count}, (_, i) => i + 1).join('<br>');
          "><code>${defaultCode}</code></pre>
        </div>
      </div>
      <p><br></p>
    `;
    insertHtmlAtCursor(blockHtml);
    logActivity(`Inserted code block [${lang}].`);
  };

  // Checklist specific tools
  // ============================================================
  // NEW: HTML Sanitization for paste handling
  // ============================================================
  const sanitizeHTML = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove dangerous elements
    const dangerousTags = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'meta', 'link', 'base'];
    dangerousTags.forEach(tag => {
      const elements = doc.querySelectorAll(tag);
      elements.forEach(el => el.remove());
    });
    
    // Remove dangerous attributes from all elements
    const allElements = doc.body.querySelectorAll('*');
    allElements.forEach(el => {
      const attrs = Array.from(el.attributes);
      attrs.forEach(attr => {
        const name = attr.name.toLowerCase();
        // Remove event handlers and javascript: URLs
        if (name.startsWith('on') || (name === 'href' && attr.value.trim().toLowerCase().startsWith('javascript:')) || name.startsWith('data-')) {
          el.removeAttribute(attr.name);
        }
      });
      // Remove style attributes that could be dangerous but keep safe ones
      const style = el.getAttribute('style');
      if (style) {
        const safeProps = ['color', 'background-color', 'background', 'font-weight', 'font-style', 'text-decoration', 'font-size', 'text-align', 'padding', 'margin', 'border'];
        const cleaned = style.split(';')
          .filter(prop => {
            const propName = prop.split(':')[0]?.trim().toLowerCase();
            return safeProps.some(safe => propName === safe);
          })
          .join(';');
        if (cleaned.trim()) {
          el.setAttribute('style', cleaned);
        } else {
          el.removeAttribute('style');
        }
      }
    });
    
    return doc.body.innerHTML;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      const sanitized = sanitizeHTML(html);
      document.execCommand('insertHTML', false, sanitized);
    } else if (text) {
      const trimmed = text.trim();
      const isSingleUrl = /^https?:\/\/[^\s<]+$/i.test(trimmed);
      if (isSingleUrl) {
        const embedHtml = getUrlEmbedHtml(trimmed);
        document.execCommand('insertHTML', false, embedHtml);
      } else {
        // Auto-detect URLs in plain text and convert to links
        const urlRegex = /https?:\/\/[^\s<]+/g;
        const processed = text.replace(urlRegex, (url) => 
          `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-sky-500 hover:text-sky-600 hover:underline font-semibold">${url}</a>`
        );
        if (processed !== text) {
          document.execCommand('insertHTML', false, processed);
        } else {
          document.execCommand('insertText', false, text);
        }
      }
    }
    
    handleContentInput();
    if (editorRef.current) {
      pushHistoryState(editorRef.current.innerHTML);
      // Re-sync line numbers for any code blocks pasted
      syncAllCodeBlockGutters(editorRef.current);
    }
    logActivity('Pasted content into document.');
  };

  // ============================================================
  // NEW: Heading detection from cursor position
  // ============================================================
  const detectCurrentHeading = (): string => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !editorRef.current) return 'p';
      
      let node: Node | null = selection.getRangeAt(0).startContainer;
      if (!editorRef.current.contains(node)) return 'p';
      
      const blockTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'DIV'];
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && blockTags.includes((node as Element).tagName)) {
          const tag = (node as Element).tagName.toLowerCase();
          return tag.startsWith('h') ? tag : 'p';
        }
        node = node.parentNode;
      }
      return 'p';
    } catch { return 'p'; }
  };

  const applyHeading = (headingTag: string) => {
    try {
      ensureSelectionInEditor();
      if (headingTag === 'p') {
        formatCommand('formatBlock', '<p>');
      } else {
        formatCommand('formatBlock', `<${headingTag}>`);
      }
      setCurrentHeading(headingTag);
      setShowHeadingDropdown(false);
      logActivity(`Applied heading: ${headingTag.toUpperCase()}`);
    } catch (e) {
      console.warn('Failed to apply heading:', e);
    }
  };

  // ============================================================
  // NEW: Per-selection font size application
  // ============================================================
  const applyFontSizeToSelection = (size: number) => {
    formatCommand('fontSize', '', { fontSize: `${size}px` });
    setFontSize(size);
    setShowFontSizeDropdown(false);
  };

  const applyTextColor = (color: string) => {
    formatCommand('foreColor', color, { color });
    setTextColor(color);
    setRecentTextColors(prev => {
      const filtered = prev.filter(c => c !== color);
      return [color, ...filtered].slice(0, 5);
    });
    setShowTextColorPicker(false);
  };

  const applyHighlightColor = (color: string) => {
    formatCommand('backColor', color, { backgroundColor: color });
    setHighlightColor(color);
    if (color !== 'transparent') {
      setRecentHighlightColors(prev => {
        const filtered = prev.filter(c => c !== color);
        return [color, ...filtered].slice(0, 5);
      });
    }
    setShowHighlightPicker(false);
  };

  // ============================================================
  // NEW: Callout block insertion
  // ============================================================
  const insertCalloutBlock = (type: 'info' | 'warning' | 'success' | 'danger') => {
    const icons: Record<string, string> = {
      info: '💡', warning: '⚠️', success: '✅', danger: '🚨'
    };
    insertHtmlAtCursor(
      `<div class="callout-block callout-${type}" contenteditable="false"><span class="callout-icon">${icons[type]}</span><div contenteditable="true" class="flex-1 outline-none">Type your ${type} callout here...</div></div><p><br></p>`
    );
    logActivity(`Inserted ${type} callout block.`);
  };

  // ============================================================
  // NEW: Close all toolbar dropdowns utility
  // ============================================================
  const closeAllToolbarDropdowns = () => {
    setShowInsertMenu(false);
    setShowAlignMenu(false);
    setShowTableMenu(false);
    setShowMoreActions(false);
    setShowTextColorPicker(false);
    setShowHighlightPicker(false);
    setShowHeadingDropdown(false);
    setShowFontSizeDropdown(false);
    setShowLinkDropdown(false);
  };

  // Update heading detection when active states update
  const updateActiveStatesEnhanced = () => {
    updateActiveStates();
    setCurrentHeading(detectCurrentHeading());
  };


  const handleAddChecklistItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemText.trim()) return;

    const newItem: ChecklistItem = {
      id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      text: newItemText.trim(),
      completed: false
    };

    const nextChecks = [...checklist, newItem];
    setChecklist(nextChecks);
    setNewItemText('');
    triggerPushUpdate({ checklistContent: nextChecks });
    logActivity(`Added checklist item: "${newItem.text}".`);
  };

  const handleToggleChecklistItem = (itemId: string) => {
    const nextChecks = checklist.map(t => 
      t.id === itemId ? { ...t, completed: !t.completed } : t
    );
    setChecklist(nextChecks);
    triggerPushUpdate({ checklistContent: nextChecks });
    logActivity(`Toggled checklist status to ${nextChecks.find(c => c.id === itemId)?.completed ? 'completed' : 'pending'}.`);
  };

  const handleDeleteChecklistItem = (itemId: string) => {
    const nextChecks = checklist.filter(t => t.id !== itemId);
    setChecklist(nextChecks);
    triggerPushUpdate({ checklistContent: nextChecks });
  };

  // Image Upload Snap tool
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const fileDataUrl = event.target.result as string;
        setPhotoUrl(fileDataUrl);
        triggerPushUpdate({ photoUrl: fileDataUrl });
        logActivity('Uploaded image attachment snap.');
      }
    };
    reader.readAsDataURL(file);
  };

  // Labels and tag settings
  const handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    const fmt = tagInput.trim().toLowerCase();
    if (fmt && !tags.includes(fmt)) {
      const nextTags = [...tags, fmt];
      setTags(nextTags);
      setTagInput('');
      triggerPushUpdate({ tags: nextTags });
      logActivity(`Assigned label tag: "#${fmt}".`);
    }
  };

  const handleRemoveTag = (idx: number) => {
    const tagVal = tags[idx];
    const nextTags = tags.filter((_, i) => i !== idx);
    setTags(nextTags);
    triggerPushUpdate({ tags: nextTags });
    logActivity(`Removed tag: "#${tagVal}".`);
  };

  const handleSelectPreexistingTag = (tagVal: string) => {
    if (!tags.includes(tagVal)) {
      const nextTags = [...tags, tagVal];
      setTags(nextTags);
      triggerPushUpdate({ tags: nextTags });
      logActivity(`Assigned folder tag "#${tagVal}".`);
    }
  };

  // Activity logger system
  const logActivity = (description: string) => {
    setActivityLogs(prev => [
      { timestamp: Date.now(), text: description },
      ...prev
    ]);
  };

  // Toggle editor expanded double arrow view
  const handleToggleFullscreen = () => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    } else {
      const nextVal = !isFullscreen;
      setIsFullscreen(nextVal);
      if (nextVal) {
        setIsNotebooksSidebarCollapsed?.(true);
        setIsPagesSidebarCollapsed?.(true);
      } else {
        setIsNotebooksSidebarCollapsed?.(false);
        setIsPagesSidebarCollapsed?.(false);
      }
      logActivity(nextVal ? 'Expanded writing canvas to distraction-free margins.' : 'Restored default sidebar rows.');
    }
  };

  // Custom checklist checkbox clicking inside editable area
  const handleContentAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      if (target.checked) {
        target.setAttribute('checked', 'checked');
      } else {
        target.removeAttribute('checked');
      }
      handleContentInput();
    }
    
    // Handle clicking internal note links in contentEditable
    const anchor = target.closest('a');
    if (anchor && anchor.getAttribute('data-note-link') === 'true') {
      const noteId = anchor.getAttribute('data-note-id');
      if (noteId) {
        e.preventDefault();
        e.stopPropagation();
        const event = new CustomEvent('open-note', { detail: { id: noteId } });
        window.dispatchEvent(event);
      }
    }
  };

  // 12 functional settings actions
  // Action 1: Favorite Toggle
  const handleFavoriteAction = (e: React.MouseEvent) => {
    onToggleFavorite(card.id!, e);
    logActivity(card.isFavorite ? 'Removed note from favorites bookshelf.' : 'Moved note to favorites bookshelf.');
    setShowMenuPopover(false);
  };

  // Action 2: Locked State Toggle
  const handleLockSetup = () => {
    if (!lockPinInput || !/^[0-9]{4}$/.test(lockPinInput)) {
      showToast("Invalid selection. PIN must be exactly 4 digits.", 'warning');
      return;
    }
    setIsLocked(true);
    setPinCode(lockPinInput);
    triggerPushUpdate({ isLocked: true, pinCode: lockPinInput });
    logActivity('Locked document behind custom PIN security.');
    setActiveDialog(null);
    setLockPinInput('');
  };

  const handleUnlockDocument = () => {
    setIsLocked(false);
    setPinCode('');
    triggerPushUpdate({ isLocked: false, pinCode: undefined });
    logActivity('Desecured document passcode lock.');
    setActiveDialog(null);
  };

  // Action 3: Version Restore Handler
  const handleRestoreSnapshot = (snap: NoteSnapshot) => {
    setTitle(snap.title);
    setTextContent(snap.textContent);
    setChecklist(snap.checklist);
    initialContentRef.current = snap.textContent;
    if (editorRef.current) {
      editorRef.current.innerHTML = snap.textContent;
    }
    triggerPushUpdate({
      title: snap.title,
      textContent: snap.textContent,
      checklistContent: snap.checklist
    });
    logActivity('Restored historical snapshot copy.');
    setActiveDialog(null);
  };

  // Action 4: Merge Note with another
  const loadMergeNotes = () => {
    if (notebooks && notebooks.length > 0 && card.notebookId) {
      // Find other notes in the current system (usually saved in localStorage)
      const rawCards = localStorage.getItem('notebook_clone_cards_catalog');
      if (rawCards) {
        const all: NoteCard[] = JSON.parse(rawCards);
        const filtered = all.filter(c => c.id !== card.id && !c.isDeleted);
        setAvailableNotesForMerge(filtered);
      }
    }
  };

  const handleMergeNotesAction = () => {
    if (!selectedNoteIdToMerge) {
      showToast('Select another note folder to merge.', 'info');
      return;
    }
    const rawCards = localStorage.getItem('notebook_clone_cards_catalog');
    if (rawCards) {
      const all: NoteCard[] = JSON.parse(rawCards);
      const targetNote = all.find(c => c.id === selectedNoteIdToMerge);
      if (targetNote) {
        if (targetNote.type === 'text') {
          const appendedText = `${textContent}<p><br></p><div class="border-t border-black/10 my-4 py-2 text-xs font-semibold text-slate-500">Merged from "${targetNote.title}":</div>${targetNote.textContent || ''}`;
          setTextContent(appendedText);
          initialContentRef.current = appendedText;
          if (editorRef.current) {
            editorRef.current.innerHTML = appendedText;
          }
          triggerPushUpdate({ textContent: appendedText, title: `${title} & ${targetNote.title}` });
          setTitle(prev => `${prev} & ${targetNote.title}`);
        } else if (targetNote.type === 'checklist' && targetNote.checklistContent) {
          const combinedList = [...checklist, ...targetNote.checklistContent.map(i => ({ ...i, id: `task-merge-${Date.now()}-${Math.random()}` }))];
          setChecklist(combinedList);
          triggerPushUpdate({ checklistContent: combinedList });
        }
        logActivity(`Merged draft with note: "${targetNote.title}".`);
        showToast('Notes consolidated successfully!', 'success');
        setActiveDialog(null);
      }
    }
  };

  // Action 5: Copy To Clipboard
  const handleCopyToClipboard = () => {
    let textToCopy = `Title: ${title}\n\n`;
    if (card.type === 'text') {
      textToCopy += editorRef.current?.innerText || textContent || '';
    } else if (card.type === 'checklist') {
      textToCopy += checklist.map(t => `${t.completed ? '[x]' : '[ ]'} ${t.text}`).join('\n');
    } else {
      textToCopy += `Visual Asset Document Card [Type: ${card.type}]`;
    }
    navigator.clipboard.writeText(textToCopy);
    logActivity('Copied document draft to notebook clipboard.');
    showToast("Draft copied to clipboard successfully!", 'success');
    setShowMenuPopover(false);
  };

  // Action 6: Print document layout pages
  const handlePrintDraft = () => {
    logActivity('Printed note sheet.');
    setShowMenuPopover(false);
    
    // Save state first
    if (editorRef.current) {
      const currentTextHtml = editorRef.current.innerHTML;
      triggerPushUpdate({ textContent: currentTextHtml });
    }
    
    setTimeout(() => {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>${title || 'Note Card Archive'}</title>
              <style>
                body { font-family: system-ui, sans-serif; padding: 40px; color: #1e293b; max-width: 800px; margin: 0 auto; line-height: 1.6; }
                h1 { border-bottom: 2px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 24px; font-weight: 800; font-size: 28px; }
                .meta { color: #64748b; font-size: 13px; margin-bottom: 30px; font-family: monospace; }
                .content { font-size: 16px; word-wrap: break-word; }
                hr { border: none; border-top: 1px dashed #cbd5e1; margin: 24px 0; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { border: 1px solid #cbd5e1; padding: 10px; text-align: left; }
                a { color: #4f46e5; text-decoration: underline; }
                .check-item { display: flex; align-items: center; gap: 10px; margin: 8px 0; }
              </style>
            </head>
            <body>
              <h1>${title || 'Untitled Writing Note'}</h1>
              <div class="meta">
                Created: ${new Date(card.createdAt || Date.now()).toLocaleString()}<br/>
                Last Modified: ${new Date().toLocaleString()}<br/>
                Word Count: ${wordCount}
              </div>
              <div class="content">
                ${card.type === 'text' ? (editorRef.current?.innerHTML || textContent) : ''}
                ${card.type === 'checklist' ? checklist.map(t => `<div class="check-item">${t.completed ? '☑' : '☐'} <span>${t.text}</span></div>`).join('') : ''}
                ${card.type === 'sketch' && sketchDataUrl ? `<img src="${sketchDataUrl}" style="max-width:100%; border-radius:12px; border:1px solid #e2e8f0" />` : ''}
                ${card.type === 'photo' && photoUrl ? `<img src="${photoUrl}" style="max-width:100%; border-radius:12px; border:1px solid #e2e8f0" />` : ''}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }, 100);
  };

  // Action 7: Move note to another book
  const handleMoveNoteToNotebook = (nbId: string) => {
    const selectedBook = notebooks.find(b => b.id === nbId);
    if (selectedBook) {
      triggerPushUpdate({ notebookId: nbId });
      logActivity(`Transferred document to notebook: "${selectedBook.title}".`);
      showToast(`Moved note successfully to "${selectedBook.title}" folder.`, 'success');
      setActiveDialog(null);
    }
  };

  // Action 8: Associated Hyperlinks references Adding
  const handleAddAssociatedLink = () => {
    if (!newLinkLabel.trim()) return;
    const cleanLabel = newLinkLabel.trim();
    setAssociatedLinks(prev => [...prev, cleanLabel]);
    setNewLinkLabel('');
    logActivity(`Added associated reference: "${cleanLabel}".`);
  };

  // Action 9: Live Document String Text Search Highlights
  const handlePerformInternalSearch = (queryStr: string) => {
    setSearchQuery(queryStr);
    if (!queryStr.trim()) {
      setSearchMatches(0);
      return;
    }
    const cleanQ = queryStr.toLowerCase().trim();
    const txt = editorRef.current?.innerText || textContent || '';
    const occurrences = (txt.toLowerCase().match(new RegExp(cleanQ, 'g')) || []).length;
    setSearchMatches(occurrences);
  };

  return (
    <div 
      className="flex-1 flex flex-col h-full overflow-hidden relative transition-all duration-300 font-sans shadow-inner"
      style={{ 
        backgroundColor: activeTheme.bgValue, 
        color: activeTheme.textValue 
      }}
      id="write-workspace-desk-viewport"
    >
      
      {/* 1. AUTO SAVING STATUS INDICATOR METADATA FLOAT TILT */}
      <div className="absolute top-2.5 left-14 z-20 flex items-center gap-2">
        {saveStatus === 'saving' && (
          <span className="text-[10px] bg-slate-900/80 text-[#cbd5c5] px-2 py-0.5 rounded-full font-bold animate-pulse text-center select-none shadow-xs">
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-[10px] bg-zinc-800 text-emerald-400 px-2.5 py-0.5 rounded-full font-bold text-center select-none shadow-xs border border-zinc-700">
            ✓ Cloud Saved
          </span>
        )}
      </div>

      {/* 2. TOP BANNER HEADER HUD */}
      <div 
        className="w-full h-14 px-5 shrink-0 flex items-center justify-between transition-colors"
        style={{ borderColor: activeTheme.accentBorder }}
      >
        {/* Top-Left: Size Scaling controls */}
        <button
          onClick={handleToggleFullscreen}
          className="p-1.5 rounded-lg transition-transform hover:scale-110 active:scale-95 cursor-pointer flex items-center justify-center border border-transparent hover:border-black/5"
          style={{ backgroundColor: activeTheme.buttonHoverBg, color: activeTheme.textValue }}
          title={isFullscreenActive ? "Exit distraction-free mode" : "Distraction-free focus full width"}
        >
          {isFullscreenActive ? <Minimize2 className="w-4.5 h-4.5" /> : <Maximize2 className="w-4.5 h-4.5" />}
        </button>

        {/* Top-Center: Notebook Card Editable Title input */}
        <div className="flex-1 max-w-sm mx-4">
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full text-center text-lg font-bold placeholder-slate-800/40 focus:outline-none focus:placeholder-transparent bg-transparent py-1 select-text transition-all focus:border-b border-black/10"
            style={{ fontFamily: fontFamily, color: activeTheme.textValue }}
          />
        </div>

        {/* Top-Right: Workspace De-selection Closing button */}
        <button
          onClick={onCancel}
          className="p-1.5 rounded-lg transition-transform hover:scale-110 active:scale-95 cursor-pointer flex items-center justify-center border border-transparent hover:border-black/10"
          style={{ backgroundColor: activeTheme.buttonHoverBg, color: activeTheme.textValue }}
          title="Deselect Note Card"
        >
          <X className="w-4.5 h-4.5" />
        </button>
      </div>

      {/* 3. PRODUCTION FLOATING TOOLBAR — NOTION/GOOGLE DOCS INSPIRED */}
      {card.type !== 'sketch' && (
        <div 
          className="w-full flex flex-col items-center justify-center py-2.5 px-4 shrink-0 transition-all select-none gap-2"
          role="toolbar"
          aria-label="Rich text formatting toolbar"
          aria-orientation="horizontal"
        >
        {/* Contextual Table Floating Actions */}
        {activeTableElement && activeCellElement && (
          <div className="flex flex-wrap items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg border text-xs bg-indigo-50/95 border-indigo-200 animate-in slide-in-from-top-2 duration-150 z-30 mb-1">
            <span className="font-extrabold text-indigo-700 mr-2 uppercase tracking-wide text-[9px]">Table:</span>
            <button onMouseDown={(e) => { e.preventDefault(); tableInsertRow(false); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Insert Row Above" aria-label="Insert row above">Row ↑</button>
            <button onMouseDown={(e) => { e.preventDefault(); tableInsertRow(true); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Insert Row Below" aria-label="Insert row below">Row ↓</button>
            <button onMouseDown={(e) => { e.preventDefault(); tableDeleteRow(); }} className="px-2 py-0.5 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold text-[11px] shadow-2xs" title="Delete Row" aria-label="Delete row">Del Row</button>
            <span className="w-px h-3 bg-slate-300 mx-1" />
            <button onMouseDown={(e) => { e.preventDefault(); tableInsertColumn(false); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Insert Column Left" aria-label="Insert column left">Col ←</button>
            <button onMouseDown={(e) => { e.preventDefault(); tableInsertColumn(true); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Insert Column Right" aria-label="Insert column right">Col →</button>
            <button onMouseDown={(e) => { e.preventDefault(); tableDeleteColumn(); }} className="px-2 py-0.5 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold text-[11px] shadow-2xs" title="Delete Column" aria-label="Delete column">Del Col</button>
            <span className="w-px h-3 bg-slate-300 mx-1" />
            <button onMouseDown={(e) => { e.preventDefault(); resizeCurrentColumn(false); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Decrease Column Width" aria-label="Decrease column width">Col Width -</button>
            <button onMouseDown={(e) => { e.preventDefault(); resizeCurrentColumn(true); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Increase Column Width" aria-label="Increase column width">Col Width +</button>
            <span className="w-px h-3 bg-slate-300 mx-1" />
            <button onMouseDown={(e) => { e.preventDefault(); tableMergeCells(); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Merge Cells" aria-label="Merge cells">Merge</button>
            <button onMouseDown={(e) => { e.preventDefault(); tableSplitCells(); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Split Cells" aria-label="Split cells">Split</button>
            
            <span className="w-px h-3 bg-slate-300 mx-1" />
            <span className="text-[9px] text-indigo-700 font-bold uppercase tracking-wider">Width:</span>
            <button onMouseDown={(e) => { e.preventDefault(); setTableWidth('100%'); }} className={`px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs ${activeTableElement.style.width === '100%' ? 'border-indigo-500 bg-indigo-50/50' : ''}`} title="100% Width">100%</button>
            <button onMouseDown={(e) => { e.preventDefault(); setTableWidth('75%'); }} className={`px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs ${activeTableElement.style.width === '75%' ? 'border-indigo-500 bg-indigo-50/50' : ''}`} title="75% Width">75%</button>
            <button onMouseDown={(e) => { e.preventDefault(); setTableWidth('50%'); }} className={`px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs ${activeTableElement.style.width === '50%' ? 'border-indigo-500 bg-indigo-50/50' : ''}`} title="50% Width">50%</button>
            
            <span className="w-px h-3 bg-slate-300 mx-1" />
            <span className="text-[9px] text-indigo-700 font-bold uppercase tracking-wider">Align:</span>
            <button onMouseDown={(e) => { e.preventDefault(); setTableAlignment('left'); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Align Table Left">Left</button>
            <button onMouseDown={(e) => { e.preventDefault(); setTableAlignment('center'); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Align Table Center">Center</button>
            <button onMouseDown={(e) => { e.preventDefault(); setTableAlignment('right'); }} className="px-2 py-0.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 border font-semibold text-[11px] shadow-2xs" title="Align Table Right">Right</button>
          </div>
        )}

        <div 
          className={`editor-toolbar-pill flex items-center gap-1 px-3 relative z-40 ${!isFullscreenActive ? 'editor-toolbar-compressed' : ''}`}
          style={{ backgroundColor: 'transparent', boxShadow: 'none', backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
          onClick={(e) => { e.stopPropagation(); }}
        >
          {/* ── GROUP 1: UNDO / REDO ── */}
          <button
            {...touchMouseActions(() => handleCustomUndo())}
            disabled={!canUndoState}
            className={`toolbar-btn ${!canUndoState ? 'disabled' : ''}`}
            title="Undo (Ctrl+Z)"
            aria-label="Undo (Ctrl+Z)"
            aria-disabled={!canUndoState}
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => handleCustomRedo())}
            disabled={!canRedoState}
            className={`toolbar-btn ${!canRedoState ? 'disabled' : ''}`}
            title="Redo (Ctrl+Y)"
            aria-label="Redo (Ctrl+Y)"
            aria-disabled={!canRedoState}
          >
            <Redo className="w-4 h-4" />
          </button>

          <span className="toolbar-sep" />

          {/* ── GROUP 2: ADD BLOCK (+) ── */}
          <div className="relative">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('insert'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('insert'); }}
              className={`toolbar-btn ${showInsertMenu ? 'active' : ''}`}
              title="Insert Block (+)"
              aria-label="Insert content block"
              aria-expanded={showInsertMenu}
              aria-haspopup="true"
            >
              <Plus className="w-4 h-4" />
            </button>
            
            {showInsertMenu && (
              <div className="toolbar-dropdown w-52" role="menu">
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Typography</span>
                <button {...touchMouseActions(() => { insertHtmlAtCursor('<p class="my-2"><br></p>'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="w-4 text-center font-bold text-slate-400">¶</span> Paragraph</button>
                <button {...touchMouseActions(() => { applyHeading('h1'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-sm text-amber-600 font-extrabold">H1</span> Heading 1</button>
                <button {...touchMouseActions(() => { applyHeading('h2'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs text-rose-500 font-bold">H2</span> Heading 2</button>
                <button {...touchMouseActions(() => { applyHeading('h3'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs text-indigo-500 font-bold">H3</span> Heading 3</button>
                
                <div className="h-px bg-slate-100 my-1" />
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Blocks</span>
                <button {...touchMouseActions(() => { formatCommand('insertUnorderedList'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><List className="w-3.5 h-3.5 text-indigo-400" /> Bullet List</button>
                <button {...touchMouseActions(() => { formatCommand('insertOrderedList'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><ListOrdered className="w-3.5 h-3.5 text-purple-400" /> Numbered List</button>
                <button {...touchMouseActions(() => { insertChecklistElement(); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><CheckSquare className="w-3.5 h-3.5 text-emerald-400" /> Checklist</button>
                <button {...touchMouseActions(() => { handleInsertTable(3, 3); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><Grid className="w-3.5 h-3.5 text-sky-400" /> Table</button>
                <button {...touchMouseActions(() => { const src = prompt("Enter image URL:"); if (src) { insertHtmlAtCursor(`<div class="my-4 text-center" contenteditable="false"><img src="${src}" class="max-w-full rounded-xl border shadow-sm mx-auto" /><p class="text-xs text-slate-400 mt-1 italic">Image</p></div><p><br></p>`); } setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs">📷</span> Image</button>
                <button {...touchMouseActions(() => { handleInsertLine(); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><Minus className="w-3.5 h-3.5 text-slate-400" /> Divider</button>
                <button {...touchMouseActions(() => { insertHtmlAtCursor('<blockquote class="border-l-4 border-indigo-500 pl-4 py-2 my-4 italic text-slate-600 bg-indigo-50/30 rounded-r-lg" contenteditable="true">"Write a quote..."</blockquote><p><br></p>'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><Quote className="w-3.5 h-3.5 text-amber-500" /> Quote</button>
                <button {...touchMouseActions(() => { insertCodeBlockElement('typescript'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><Code className="w-3.5 h-3.5 text-violet-400" /> Code Block</button>
                
                <div className="h-px bg-slate-100 my-1" />
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Callouts</span>
                <button {...touchMouseActions(() => { insertCalloutBlock('info'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs">💡</span> Info Callout</button>
                <button {...touchMouseActions(() => { insertCalloutBlock('warning'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs">⚠️</span> Warning Callout</button>
                <button {...touchMouseActions(() => { insertCalloutBlock('success'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs">✅</span> Success Callout</button>
                <button {...touchMouseActions(() => { insertCalloutBlock('danger'); setShowInsertMenu(false); })} className="toolbar-dropdown-item" role="menuitem"><span className="text-xs">🚨</span> Danger Callout</button>
              </div>
            )}
          </div>

          <span className="toolbar-sep" />

          {/* ── GROUP 3: INLINE FORMATTING ── */}
          <button
            {...touchMouseActions(() => formatCommand('bold'))}
            className={`toolbar-btn ${activeStyles.bold ? 'active' : ''}`}
            title="Bold (Ctrl+B)"
            aria-label="Bold (Ctrl+B)"
            aria-pressed={activeStyles.bold}
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => formatCommand('italic'))}
            className={`toolbar-btn ${activeStyles.italic ? 'active' : ''}`}
            title="Italic (Ctrl+I)"
            aria-label="Italic (Ctrl+I)"
            aria-pressed={activeStyles.italic}
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => formatCommand('underline'))}
            className={`toolbar-btn ${activeStyles.underline ? 'active' : ''}`}
            title="Underline (Ctrl+U)"
            aria-label="Underline (Ctrl+U)"
            aria-pressed={activeStyles.underline}
          >
            <Underline className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => formatCommand('strikeThrough'))}
            className={`toolbar-btn toolbar-responsive-hide ${activeStyles.strikethrough ? 'active' : ''}`}
            title="Strikethrough (Alt+Shift+5)"
            aria-label="Strikethrough (Alt+Shift+5)"
            aria-pressed={activeStyles.strikethrough}
          >
            <Strikethrough className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => toggleInlineCode())}
            className={`toolbar-btn toolbar-responsive-hide ${activeStyles.code ? 'active' : ''}`}
            title="Inline Code"
            aria-label="Toggle inline code formatting"
            aria-pressed={activeStyles.code}
          >
            <Code className="w-4 h-4" />
          </button>

          <span className="toolbar-sep" />

          {/* ── GROUP 4: LINK & EMBEDS ── */}
          <div className="relative">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('linkDropdown'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('linkDropdown'); }}
              className={`toolbar-btn ${showLinkDropdown ? 'active' : ''}`}
              title="Insert Link or Embed (Ctrl+K)"
              aria-label="Insert link or embed dropdown"
              aria-expanded={showLinkDropdown}
              aria-haspopup="true"
            >
              <Link className="w-4 h-4 text-sky-500" />
            </button>
            
            {showLinkDropdown && (
              <div className="toolbar-dropdown w-44" role="menu">
                <button 
                  {...touchMouseActions(() => { openLinkModal(); setShowLinkDropdown(false); })} 
                  className="toolbar-dropdown-item" 
                  role="menuitem"
                >
                  <Link className="w-3.5 h-3.5 text-sky-500" /> Link URL
                </button>
                <button 
                  {...touchMouseActions(() => { openNoteLinkModal(); setShowLinkDropdown(false); })} 
                  className="toolbar-dropdown-item" 
                  role="menuitem"
                >
                  <FileText className="w-3.5 h-3.5 text-amber-500" /> Link Note
                </button>
                <button 
                  {...touchMouseActions(() => { openEmbedModal(); setShowLinkDropdown(false); })} 
                  className="toolbar-dropdown-item" 
                  role="menuitem"
                >
                  <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> Embed URL
                </button>
              </div>
            )}
          </div>

          <button
            {...touchMouseActions(() => {
              insertHtmlAtCursor('<blockquote class="border-l-4 border-indigo-500 pl-4 py-2 my-4 italic text-slate-655 bg-indigo-50/30 rounded-r-lg" contenteditable="true">"Write a quote..."</blockquote><p><br></p>');
            })}
            className="toolbar-btn"
            title="Quote Block"
            aria-label="Insert quote block"
          >
            <Quote className="w-4 h-4 text-amber-555" />
          </button>

          <span className="toolbar-sep toolbar-responsive-hide" />

          {/* ── GROUP 5: TEXT ALIGNMENT ── */}
          <div className="relative toolbar-responsive-hide">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('align'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('align'); }}
              className={`toolbar-btn ${showAlignMenu ? 'active' : ''}`}
              title="Text Alignment"
              aria-label="Text alignment options"
              aria-expanded={showAlignMenu}
              aria-haspopup="true"
            >
              {activeStyles.alignCenter ? <AlignCenter className="w-4 h-4" /> : 
               activeStyles.alignRight ? <AlignRight className="w-4 h-4" /> : 
               activeStyles.alignJustify ? <AlignJustify className="w-4 h-4" /> :
               <AlignLeft className="w-4 h-4" />}
            </button>
            
            {showAlignMenu && (
              <div className="toolbar-dropdown w-36" role="menu">
                <button {...touchMouseActions(() => { formatCommand('justifyLeft'); setShowAlignMenu(false); })} className={`toolbar-dropdown-item ${activeStyles.alignLeft ? 'active' : ''}`} role="menuitem" aria-label="Align left"><AlignLeft className="w-3.5 h-3.5" /> Left</button>
                <button {...touchMouseActions(() => { formatCommand('justifyCenter'); setShowAlignMenu(false); })} className={`toolbar-dropdown-item ${activeStyles.alignCenter ? 'active' : ''}`} role="menuitem" aria-label="Align center"><AlignCenter className="w-3.5 h-3.5" /> Center</button>
                <button {...touchMouseActions(() => { formatCommand('justifyRight'); setShowAlignMenu(false); })} className={`toolbar-dropdown-item ${activeStyles.alignRight ? 'active' : ''}`} role="menuitem" aria-label="Align right"><AlignRight className="w-3.5 h-3.5" /> Right</button>
                <button {...touchMouseActions(() => { formatCommand('justifyFull'); setShowAlignMenu(false); })} className={`toolbar-dropdown-item ${activeStyles.alignJustify ? 'active' : ''}`} role="menuitem" aria-label="Justify"><AlignJustify className="w-3.5 h-3.5" /> Justify</button>
              </div>
            )}
          </div>

          <span className="toolbar-sep toolbar-responsive-hide" />

          {/* ── GROUP 6: LISTS ── */}
          <button
            {...touchMouseActions(() => formatCommand('insertUnorderedList'))}
            className={`toolbar-btn toolbar-responsive-hide ${activeStyles.bulletList ? 'active' : ''}`}
            title="Bullet List"
            aria-label="Toggle bullet list"
            aria-pressed={activeStyles.bulletList}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => formatCommand('insertOrderedList'))}
            className={`toolbar-btn toolbar-responsive-hide ${activeStyles.orderedList ? 'active' : ''}`}
            title="Numbered List"
            aria-label="Toggle numbered list"
            aria-pressed={activeStyles.orderedList}
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          <button
            {...touchMouseActions(() => insertChecklistElement())}
            className="toolbar-btn toolbar-mobile-hide"
            title="Checklist"
            aria-label="Insert checklist item"
          >
            <CheckSquare className="w-4 h-4 text-emerald-500" />
          </button>

          <span className="toolbar-sep toolbar-responsive-hide" />

          {/* ── GROUP 7: TABLE ── */}
          <div className="relative toolbar-mobile-hide">
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                setHoverTableRows(3);
                setHoverTableCols(3);
                toggleMenu('table');
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                setHoverTableRows(3);
                setHoverTableCols(3);
                toggleMenu('table');
              }}
              className={`toolbar-btn ${showTableMenu ? 'active' : ''}`}
              title="Insert Table"
              aria-label="Insert table"
              aria-expanded={showTableMenu}
              aria-haspopup="true"
            >
              <Grid className="w-4 h-4 text-indigo-500" />
            </button>
            
            {showTableMenu && (
              <div 
                className="absolute left-1/2 top-full -translate-x-1/2 z-50 mt-3 bg-white border border-slate-200 rounded-lg p-3 shadow-xl select-none dark:bg-slate-900 dark:border-slate-800" 
                role="menu"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                style={{ width: '220px' }}
              >
                {/* Grid Container */}
                <div 
                  className="touch-none"
                  onTouchMove={handleTouchMoveNoteTable}
                  onTouchEnd={handleTouchEndNoteTable}
                >
                  <div 
                    className="grid gap-[3px]"
                    style={{ 
                      gridTemplateColumns: `repeat(${TABLE_PICKER_COLS}, 16px)`, 
                      justifyContent: 'center' 
                    }}
                  >
                    {Array.from({ length: TABLE_PICKER_ROWS * TABLE_PICKER_COLS }).map((_, idx) => {
                      const row = Math.floor(idx / TABLE_PICKER_COLS) + 1;
                      const col = (idx % TABLE_PICKER_COLS) + 1;
                      const isSelected = row <= hoverTableRows && col <= hoverTableCols;
                      return (
                        <button
                          type="button"
                          key={idx}
                          data-row={row - 1}
                          data-col={col - 1}
                          onPointerEnter={() => { 
                            setHoverTableRows(row); 
                            setHoverTableCols(col); 
                          }}
                          onPointerDown={(e) => {
                            if (e.pointerType === 'mouse' && e.button !== 0) return;
                            e.preventDefault();
                            e.stopPropagation();
                            if (editorRef.current && document.activeElement !== editorRef.current) {
                              editorRef.current.focus();
                            }
                            ensureSelectionInEditor();
                            handleInsertTable(col, row);
                            setShowTableMenu(false);
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (editorRef.current && document.activeElement !== editorRef.current) {
                              editorRef.current.focus();
                            }
                            ensureSelectionInEditor();
                            handleInsertTable(col, row);
                            setShowTableMenu(false);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '1px',
                            border: '1px solid',
                            borderColor: isSelected ? '#3b82f6' : '#e5e7eb',
                            backgroundColor: isSelected ? '#dbeafe' : '#f9fafb',
                            cursor: 'pointer',
                            transition: 'all 100ms ease',
                          }}
                          className="dark:bg-slate-950 dark:border-slate-800"
                          title={`${col} x ${row} table`}
                          aria-label={`${col} columns by ${row} rows`}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 text-center text-xs font-bold text-slate-700 dark:text-slate-350">
                  {hoverTableCols} x {hoverTableRows}
                </div>
              </div>
            )}
          </div>

          <span className="toolbar-sep toolbar-responsive-hide" />

          {/* ── GROUP 8: TEXT COLOR ── */}
          <div className="relative toolbar-responsive-hide">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('textColor'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('textColor'); }}
              className={`toolbar-btn ${showTextColorPicker ? 'active' : ''}`}
              title="Text Color"
              aria-label="Text color"
              aria-expanded={showTextColorPicker}
              aria-haspopup="true"
            >
              <div className="flex flex-col items-center gap-0.5">
                <Type className="w-3.5 h-3.5" />
                <div className="w-3.5 h-[3px] rounded-full" style={{ backgroundColor: textColor }} />
              </div>
            </button>
            
            {showTextColorPicker && (
              <div className="toolbar-dropdown w-56 p-3 select-text" role="menu">
                <span className="text-[9px] font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Text Color</span>
                
                {recentTextColors.length > 0 && (
                  <>
                    <span className="text-[8px] font-semibold text-slate-400 block mb-1">Recent</span>
                    <div className="flex gap-1.5 mb-2">
                      {recentTextColors.map((col) => (
                        <button key={col} {...touchMouseActions(() => applyTextColor(col))} className={`color-swatch ${textColor === col ? 'selected' : ''}`} style={{ backgroundColor: col }} title={col} aria-label={`Text color ${col}`} />
                      ))}
                    </div>
                  </>
                )}
                
                <div className="grid grid-cols-10 gap-1 mb-2.5">
                  {TEXT_COLOR_PRESETS.map((col) => (
                    <button key={col} {...touchMouseActions(() => applyTextColor(col))} className={`color-swatch ${textColor === col ? 'selected' : ''}`} style={{ backgroundColor: col }} title={col} aria-label={`Text color ${col}`} />
                  ))}
                </div>
                
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] font-bold text-slate-400 shrink-0">HEX</span>
                  <input
                    type="text"
                    placeholder="#000000"
                    value={customHexInput}
                    onChange={(e) => setCustomHexInput(e.target.value)}
                    className="flex-1 text-xs font-mono px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-slate-50 w-20"
                    maxLength={7}
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); if (/^#[0-9a-fA-F]{3,6}$/.test(customHexInput)) { applyTextColor(customHexInput); setCustomHexInput(''); } }}
                    className="px-2 py-1 bg-indigo-600 text-white text-[10px] font-bold rounded-lg hover:bg-indigo-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── GROUP 8b: HIGHLIGHT COLOR ── */}
          <div className="relative toolbar-responsive-hide">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('highlightColor'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('highlightColor'); }}
              className={`toolbar-btn ${showHighlightPicker ? 'active' : ''}`}
              title="Highlight Color"
              aria-label="Highlight color"
              aria-expanded={showHighlightPicker}
              aria-haspopup="true"
            >
              <div className="flex flex-col items-center gap-0.5">
                <Highlighter className="w-3.5 h-3.5" />
                <div className="w-3.5 h-[3px] rounded-full border border-slate-200" style={{ backgroundColor: highlightColor === 'transparent' ? '#e2e8f0' : highlightColor }} />
              </div>
            </button>
            
            {showHighlightPicker && (
              <div className="toolbar-dropdown w-56 p-3 select-text" role="menu">
                <span className="text-[9px] font-bold text-slate-400 block mb-1.5 uppercase tracking-wider">Highlight</span>
                
                {recentHighlightColors.length > 0 && (
                  <>
                    <span className="text-[8px] font-semibold text-slate-400 block mb-1">Recent</span>
                    <div className="flex gap-1.5 mb-2">
                      {recentHighlightColors.map((col) => (
                        <button key={col} {...touchMouseActions(() => applyHighlightColor(col))} className={`color-swatch ${highlightColor === col ? 'selected' : ''}`} style={{ backgroundColor: col }} title={col} aria-label={`Highlight ${col}`} />
                      ))}
                    </div>
                  </>
                )}
                
                <div className="grid grid-cols-8 gap-1 mb-2.5">
                  {HIGHLIGHT_COLOR_PRESETS.map((col) => (
                    <button key={col} {...touchMouseActions(() => applyHighlightColor(col))} className={`color-swatch ${highlightColor === col ? 'selected' : ''}`} style={{ backgroundColor: col === 'transparent' ? '#ffffff' : col, border: col === 'transparent' ? '2px dashed #cbd5e1' : undefined }} title={col === 'transparent' ? 'None' : col} aria-label={col === 'transparent' ? 'Remove highlight' : `Highlight ${col}`}>
                      {col === 'transparent' && <span className="text-[8px] text-slate-400 font-bold">∅</span>}
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] font-bold text-slate-400 shrink-0">HEX</span>
                  <input type="text" placeholder="#fef08a" value={customHexInput} onChange={(e) => setCustomHexInput(e.target.value)} className="flex-1 text-xs font-mono px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-slate-50 w-20" maxLength={7} />
                  <button onMouseDown={(e) => { e.preventDefault(); if (/^#[0-9a-fA-F]{3,6}$/.test(customHexInput)) { applyHighlightColor(customHexInput); setCustomHexInput(''); } }} className="px-2 py-1 bg-indigo-600 text-white text-[10px] font-bold rounded-lg hover:bg-indigo-700">Apply</button>
                </div>
              </div>
            )}
          </div>

          <span className="toolbar-sep toolbar-responsive-hide" />

          {/* ── GROUP 9: HEADING DROPDOWN ── */}
          <div className="relative toolbar-responsive-hide">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('heading'); setCurrentHeading(detectCurrentHeading()); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('heading'); setCurrentHeading(detectCurrentHeading()); }}
              className={`toolbar-btn ${showHeadingDropdown ? 'active' : ''} !w-auto px-2 gap-1`}
              title="Heading Level"
              aria-label="Heading level"
              aria-expanded={showHeadingDropdown}
              aria-haspopup="listbox"
              style={{ width: 'auto', minWidth: '32px' }}
            >
              <Heading className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold hidden sm:inline">{currentHeading === 'p' ? 'P' : currentHeading.toUpperCase()}</span>
              <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
            </button>
            
            {showHeadingDropdown && (
              <div className="toolbar-dropdown w-44" role="listbox" aria-label="Heading options">
                {HEADING_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    {...touchMouseActions(() => applyHeading(opt.value))}
                    className={`toolbar-dropdown-item ${currentHeading === opt.value ? 'active' : ''}`}
                    role="option"
                    aria-selected={currentHeading === opt.value}
                    style={{
                      fontSize: opt.value === 'h1' ? '18px' : opt.value === 'h2' ? '16px' : opt.value === 'h3' ? '15px' : opt.value === 'h4' ? '14px' : '12px',
                      fontWeight: opt.value === 'p' ? 500 : opt.value.startsWith('h') ? 700 : 500,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── GROUP 9b: FONT SIZE DROPDOWN ── */}
          <div className="relative toolbar-mobile-hide">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('fontSize'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('fontSize'); }}
              className={`toolbar-btn ${showFontSizeDropdown ? 'active' : ''} !w-auto px-2 gap-0.5`}
              title="Font Size"
              aria-label="Font size"
              aria-expanded={showFontSizeDropdown}
              aria-haspopup="listbox"
              style={{ width: 'auto', minWidth: '32px' }}
            >
              <span className="text-[11px] font-bold">{fontSize}</span>
              <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
            </button>
            
            {showFontSizeDropdown && (
              <div className="toolbar-dropdown w-32 max-h-64 overflow-y-auto" role="listbox" aria-label="Font size options">
                {FONT_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    {...touchMouseActions(() => applyFontSizeToSelection(size))}
                    className={`toolbar-dropdown-item ${fontSize === size ? 'active' : ''}`}
                    role="option"
                    aria-selected={fontSize === size}
                  >
                    <span className="font-bold">{size}</span>
                    <span className="text-slate-400 text-[10px]">px</span>
                  </button>
                ))}
                <div className="h-px bg-slate-100 my-1" />
                <div className="flex items-center gap-1 px-1">
                  <input
                    type="number"
                    min={8}
                    max={120}
                    placeholder="Custom"
                    className="w-full text-xs font-mono px-2 py-1 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-slate-50"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = parseInt((e.target as HTMLInputElement).value);
                        if (val >= 8 && val <= 120) { applyFontSizeToSelection(val); }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <span className="toolbar-sep" />

          {/* ── GROUP 10: MORE ACTIONS ── */}
          <div className="relative">
            <button
              onMouseDown={(e) => { e.preventDefault(); toggleMenu('more'); }}
              onTouchStart={(e) => { e.preventDefault(); toggleMenu('more'); }}
              className={`toolbar-btn ${showMoreActions ? 'active' : ''}`}
              title="More Options"
              aria-label="More formatting options"
              aria-expanded={showMoreActions}
              aria-haspopup="true"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            
            {showMoreActions && (
              <div className="toolbar-dropdown w-64 max-h-[480px] overflow-y-auto p-3" style={{ left: 'auto', right: 0, transform: 'none' }} role="menu">
                {/* Stats */}
                <div className="bg-gradient-to-r from-slate-50 to-indigo-50/30 rounded-xl p-2.5 mb-3 text-[11px] font-semibold text-slate-600 flex justify-between items-center border border-slate-100">
                  <span>Words: <b className="text-slate-900">{wordCount || (editorRef.current?.innerText.trim().split(/\s+/).filter(Boolean).length || 0)}</b></span>
                  <span className="w-px h-3 bg-slate-200" />
                  <span>Chars: <b className="text-slate-900">{charCount || (editorRef.current?.innerText.length || 0)}</b></span>
                </div>

                {/* Clear Formatting */}
                <button
                  onMouseDown={(e) => { e.preventDefault(); formatCommand('removeFormat'); setTextColor('#000000'); setHighlightColor('transparent'); setShowMoreActions(false); }}
                  className="toolbar-dropdown-item text-rose-600 hover:bg-rose-50 mb-1"
                  role="menuitem"
                  aria-label="Clear formatting"
                >
                  <Eraser className="w-3.5 h-3.5" /> Clear Formatting
                </button>

                <div className="h-px bg-slate-100 my-1.5" />

                {/* Duplicate Block */}
                <button
                  onMouseDown={(e) => { e.preventDefault(); if (card.id) { onDuplicate(card as NoteCard, e as unknown as React.MouseEvent); } setShowMoreActions(false); }}
                  className="toolbar-dropdown-item"
                  role="menuitem"
                  aria-label="Duplicate block"
                >
                  <Copy className="w-3.5 h-3.5 text-indigo-400" /> Duplicate Block
                </button>

                {/* Copy Content */}
                <button
                  onMouseDown={(e) => { e.preventDefault(); handleCopyToClipboard(); setShowMoreActions(false); }}
                  className="toolbar-dropdown-item"
                  role="menuitem"
                  aria-label="Copy to clipboard"
                >
                  <ClipboardCopy className="w-3.5 h-3.5 text-sky-400" /> Copy to Clipboard
                </button>

                <div className="h-px bg-slate-100 my-1.5" />
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Appearance</span>

                {/* Font Family */}
                <div className="grid grid-cols-2 gap-1 mb-2 px-1">
                  {[
                    { label: 'Sans', value: '"Inter", sans-serif' },
                    { label: 'Serif', value: 'Georgia, serif' },
                    { label: 'Mono', value: '"JetBrains Mono", monospace' },
                    { label: 'System', value: 'system-ui' }
                  ].map((font) => (
                    <button
                      key={font.value}
                      onMouseDown={(e) => { e.preventDefault(); setFontFamily(font.value); }}
                      className={`px-2 py-1.5 border rounded-lg text-[10px] font-semibold text-center transition-colors ${fontFamily === font.value ? 'bg-indigo-600 border-indigo-700 text-white' : 'border-slate-150 hover:bg-slate-50 text-slate-700'}`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>

                {/* Line Spacing */}
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Line Spacing</span>
                <div className="grid grid-cols-3 gap-1 mb-2.5 px-1">
                  {[
                    { label: '1.0', val: '1' },
                    { label: '1.5', val: '1.5' },
                    { label: '2.0', val: '2' }
                  ].map((lh) => (
                    <button key={lh.val} onMouseDown={(e) => { e.preventDefault(); if (editorRef.current) editorRef.current.style.lineHeight = lh.val; logActivity(`Line spacing: ${lh.label}`); }} className="px-1.5 py-1 border border-slate-150 hover:bg-slate-50 rounded-lg text-[10px] font-bold text-slate-600 text-center">{lh.label}</button>
                  ))}
                </div>

                <div className="h-px bg-slate-100 my-1.5" />
                <span className="text-[9px] font-bold text-slate-400 block px-2.5 pb-1 uppercase tracking-wider">Export</span>

                <button onMouseDown={(e) => { e.preventDefault(); exportAsHTML(); setShowMoreActions(false); }} className="toolbar-dropdown-item" role="menuitem" aria-label="Export as HTML">
                  <FileDown className="w-3.5 h-3.5 text-emerald-400" /> Export HTML
                </button>
                <button onMouseDown={(e) => { e.preventDefault(); exportAsMarkdown(); setShowMoreActions(false); }} className="toolbar-dropdown-item" role="menuitem" aria-label="Export as Markdown">
                  <FileText className="w-3.5 h-3.5 text-blue-400" /> Export Markdown
                </button>
                <button onMouseDown={(e) => { e.preventDefault(); handlePrintDraft(); setShowMoreActions(false); }} className="toolbar-dropdown-item" role="menuitem" aria-label="Print or export PDF">
                  <Printer className="w-3.5 h-3.5 text-purple-400" /> Print / PDF
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
      )}

      {/* 4. CORE CANVAS WORKSPACE AREA (MARGIN RE-CALIBRATED) */}
      <div className="flex-1 overflow-y-auto px-6 md:px-14 py-6 flex flex-col auto-scrollbar select-text mb-4">
        
        {/* If locked, display PIN cover HUD */}
        {isLocked ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto select-none">
            <div className="w-16 h-16 bg-amber-500/10 border border-amber-300 rounded-2xl flex items-center justify-center mb-4 text-yellow-600 animate-bounce">
              <Lock className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900 tracking-tight">Security Code Active</h3>
            <p className="text-xs text-slate-600/80 mt-1 mb-5">Enter your 4-digit PIN access key code to reveal note contents.</p>
            <input
              type="password"
              maxLength={4}
              placeholder="••••"
              value={lockPinInput}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setLockPinInput(val);
                if (val === pinCode) {
                  setIsLocked(false);
                  setLockPinInput('');
                  logActivity('Unlocked passcode document viewport.');
                }
              }}
              className="w-32 tracking-wider text-xl font-mono text-center p-2.5 border rounded-xl bg-white select-text text-slate-850 shadow-xs border-slate-300/80 focus:outline-none"
            />
          </div>
        ) : (
          <div className="flex-1 min-h-[360px] max-w-3xl mx-auto w-full relative">
            
            {/* INLINE TEXT TYPE */}
            {card.type === 'text' && (
              <div 
                className="w-full h-full flex flex-col gap-1 pr-1.5"
                id="text-workspace-editable-canvas"
              >
                <div
                  ref={editorRef}
                  contentEditable={true}
                  onInput={handleContentInput}
                  onPaste={handlePaste}
                  onBlur={handleContentBlur}
                  onKeyDown={handleEditorKeyDown}
                  onKeyUp={() => {
                    updateActiveStates();
                    updateActiveTableSelection();
                  }}
                  onMouseUp={() => {
                    updateActiveStates();
                    updateActiveTableSelection();
                  }}
                  onFocus={() => {
                    updateActiveStates();
                    updateActiveTableSelection();
                  }}
                  onClick={(e) => {
                    handleContentAreaClick(e);
                    updateActiveStates();
                    updateActiveTableSelection();
                  }}
                  onMouseMove={handleEditorMouseMove}
                  onMouseLeave={handleEditorMouseLeave}
                  className="w-full h-full min-h-[400px] text-base md:text-[17px] leading-relaxed focus:outline-none bg-transparent resize-none outline-none font-medium text-left select-text"
                  placeholder="Start jotting down your thoughts..."
                  style={{ 
                    fontFamily: fontFamily, 
                    fontSize: `${fontSize}px`,
                    outline: 'none',
                    color: activeTheme.textValue
                  }}
                  id="notes-content-editable-area"
                  role="textbox"
                  aria-multiline="true"
                  aria-label="Document editor"
                  suppressContentEditableWarning={true}
                />

                {/* Hover Preview Tooltip Card */}
                {hoveredNotePreview && (
                  <div 
                    className="absolute z-50 bg-white border border-slate-200 shadow-xl rounded-xl p-3.5 w-64 text-left pointer-events-none select-none animate-in fade-in zoom-in-95 duration-100"
                    style={{ left: `${hoveredNotePreview.x}px`, top: `${hoveredNotePreview.y}px` }}
                  >
                    <div className="font-bold text-xs text-slate-800 border-b pb-1 mb-1.5 truncate">
                      📄 {hoveredNotePreview.title}
                    </div>
                    <div className="text-[10px] leading-normal text-slate-500 line-clamp-3">
                      {hoveredNotePreview.summary}
                    </div>
                  </div>
                )}

                {/* Linked References (Backlinks) Drawer */}
                {backlinks.length > 0 && (
                  <div className="mt-8 border-t border-black/10 pt-4 font-sans" contentEditable={false}>
                    <span className="text-[10px] font-bold text-slate-550 uppercase tracking-wide block mb-2">Linked References ({backlinks.length})</span>
                    <div className="flex flex-wrap gap-2">
                      {backlinks.map((link) => (
                        <button
                          key={link.id}
                          onClick={(e) => {
                            e.preventDefault();
                            window.dispatchEvent(new CustomEvent('open-note', { detail: { id: link.id } }));
                          }}
                          className="text-xs bg-white/80 hover:bg-indigo-50 border border-slate-200 text-indigo-700 rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 cursor-pointer transition-colors shadow-xxs"
                        >
                          📄 {link.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* INLINE CHECKLIST TYPE */}
            {card.type === 'checklist' && (
              <div className="max-w-xl mx-auto flex flex-col gap-4 py-2 w-full">
                <form onSubmit={handleAddChecklistItem} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add checklist item..."
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    className="flex-1 text-sm bg-white/70 border border-black/10 rounded-xl px-4 py-2.5 placeholder-slate-700/50 focus:outline-none focus:ring-1 focus:ring-black/10 text-slate-900 select-text font-bold"
                  />
                  <button
                    type="submit"
                    className="p-3 bg-zinc-800 hover:bg-black text-white rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer flex items-center justify-center shrink-0 font-bold"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </form>

                <div className="flex flex-col gap-2 max-h-[380px] overflow-y-auto pr-1 auto-scrollbar">
                  {checklist.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between bg-white/40 hover:bg-white/60 p-2.5 px-3.5 rounded-xl border border-black/5 transition-all"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleToggleChecklistItem(item.id)}
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all shrink-0 ${
                            item.completed ? 'bg-zinc-800 border-zinc-850 text-white' : 'border-slate-500 hover:border-black'
                          }`}
                        >
                          {item.completed && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                        </button>
                        <span 
                          className={`text-sm flex-1 truncate ${item.completed ? 'line-through text-slate-500/80 font-semibold' : 'font-bold'}`}
                          style={item.completed ? undefined : { color: activeTheme.textValue }}
                        >
                          {item.text}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteChecklistItem(item.id)}
                        className="text-slate-500 hover:text-rose-600 p-1 rounded-sm shrink-0"
                        title="Delete item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {checklist.length === 0 && (
                    <div className="text-center py-12 text-slate-600/80 text-xs font-bold">
                      The task list is currently empty. Add work items to track progress.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* INLINE DRAWING SKETCHPAD TYPE */}
            {card.type === 'sketch' && (
              <div className="max-w-4xl mx-auto flex flex-col h-full justify-center">
                <SketchPad
                  initialDataUrl={sketchDataUrl}
                  onSave={(dataUrl) => {
                    setSketchDataUrl(dataUrl);
                    triggerPushUpdate({ sketchDataUrl: dataUrl });
                    logActivity('Updated canvas drawing details.');
                  }}
                />
              </div>
            )}

            {/* INLINE VOICE AUDIO TYPE */}
            {card.type === 'audio' && (
              <div className="max-w-md mx-auto py-6 flex flex-col justify-center h-full">
                <AudioRecorder
                  initialAudioUrl={audioUrl}
                  initialDuration={audioDuration}
                  onSave={(url, dur) => {
                    setAudioUrl(url);
                    setAudioDuration(dur);
                    triggerPushUpdate({ audioUrl: url, audioDuration: dur });
                    logActivity('Recorded beautiful voice audio stream.');
                  }}
                  onDelete={() => {
                    setAudioUrl('');
                    setAudioDuration(0);
                    triggerPushUpdate({ audioUrl: '', audioDuration: 0 });
                    logActivity('Removed audio memo attachment.');
                  }}
                />
              </div>
            )}

            {/* INLINE ATTACHMENT PHOTO SNAP TYPE */}
            {card.type === 'photo' && (
              <div className="max-w-xl mx-auto flex flex-col gap-4 justify-center h-full">
                {!photoUrl ? (
                  <div className="border border-dashed border-black/15 bg-white/40 hover:bg-white/60 rounded-2xl p-12 flex flex-col items-center justify-center text-center transition-all select-none">
                    <svg className="w-12 h-12 text-slate-655 mb-2.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.128-.77.262-1.15.396a2.31 2.31 0 00-1.536 2.184v5.03c0 1.018.667 1.894 1.636 2.112l.142.032c1.785.4 3.57.8 5.355 1.2a1.47 1.47 0 001.636-1.112v-5.03c0-1.018-.667-1.894-1.636-2.112l-.142-.032a1.47 1.47 0 00-1.636 1.112m0 0l-1-.2M17 11a4 4 0 11-8 0 4 4 0 018 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-xs font-bold text-slate-800">Upload photograph snapshot</span>
                    <p className="text-[10px] text-slate-500 mt-1 mb-4">Accepts PNG, JPG, GIF or WEBP format</p>
                    <input
                      type="file"
                      id="photo-workspace-loader"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="photo-workspace-loader"
                      className="px-4 py-2 bg-zinc-800 hover:bg-black text-white text-xs font-bold rounded-lg cursor-pointer shadow-xs transition-transform hover:scale-105 active:scale-95"
                    >
                      Select File
                    </label>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="relative rounded-2xl border border-black/10 overflow-hidden bg-black/5 flex items-center justify-center max-h-[300px] p-2">
                      <img
                        src={photoUrl}
                        alt="Workspace snapshot attachment"
                        className="max-h-[280px] object-contain rounded-xl"
                        referrerPolicy="no-referrer"
                      />
                      <button
                        onClick={() => {
                          setPhotoUrl('');
                          triggerPushUpdate({ photoUrl: '' });
                          logActivity('Removed photograph snap attachment.');
                        }}
                        className="absolute right-4 top-4 p-2 bg-black/75 hover:bg-black text-white rounded-full transition-colors font-bold cursor-pointer shadow"
                        title="Delete Image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-center gap-1.5 select-none">
                      <input
                        type="file"
                        id="photo-workspace-replace-loader"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="photo-workspace-replace-loader"
                        className="text-xs text-zinc-900 border-b border-zinc-900 cursor-pointer font-bold hover:text-black mt-1"
                      >
                        Choose another photo
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ASSOCIATED LINKS REFERENCES LOWER COMPARTMENT FOOTNOTE */}
            {associatedLinks.length > 0 && (
              <div className="mt-8 border-t border-black/10 pt-4">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-2">Associated Reference Linkages</span>
                <div className="flex flex-wrap gap-2">
                  {associatedLinks.map((lnk, i) => (
                    <a 
                      key={i} 
                      href={lnk.startsWith('http') ? lnk : `https://${lnk}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs bg-indigo-500/10 border border-indigo-400/20 text-indigo-700 rounded-full px-3 py-1 font-semibold flex items-center gap-1 hover:bg-indigo-500/20"
                    >
                      🔗 {lnk}
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

      </div>

      {/* 5. FLOATING BOTTOM CHICKLET ALARMS / LABELS STATUSES DISPLAY ROW */}
      <div className="px-6 md:px-14 flex flex-wrap gap-3 mb-2 shrink-0 select-none">
        {/* Render tags assigned */}
        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {tags.map((tag, idx) => (
              <span 
                key={idx}
                className="bg-black/5 text-slate-800 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 border border-black/5"
              >
                <Tag className="w-2.5 h-2.5 text-slate-500" />
                {tag}
                <button onClick={() => handleRemoveTag(idx)} className="hover:text-rose-600 font-extrabold ml-1 cursor-pointer">×</button>
              </span>
            ))}
          </div>
        )}

        {/* Locked security indicator pin detail */}
        {pinCode && (
          <span className="bg-amber-600/10 text-amber-800 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-amber-500/10">
            <Lock className="w-2.5 h-2.5" />
            Security Locked: {pinCode || '••••'}
          </span>
        )}

        {/* Alarm alarm scheduler notifications details */}
        {reminderTime && (
          <span className="bg-indigo-650/10 text-indigo-850 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-indigo-505/10">
            <Clock className="w-2.5 h-2.5 text-indigo-600" />
            Scheduled Reminder: {new Date(reminderTime).toLocaleDateString()} {new Date(reminderTime).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* 6. FLOATING BOTTOM RIBBON ACTION BAR (SCREENSHOT 1) */}
      <div 
        className="border-t py-3.5 px-5 flex items-center justify-center relative shrink-0 select-none transition-colors"
        style={{ 
          borderColor: activeTheme.accentBorder,
          backgroundColor: 'rgba(0,0,0,0.02)'
        }}
      >
        {/* Capsule Action bar container */}
        <div 
          className="flex items-center gap-4 bg-white/95 px-4.5 py-2.5 rounded-full shadow-lg border relative"
          style={{ borderColor: activeTheme.accentBorder }}
        >
          {/* Sparkles (New) AI helper tool */}
          <div className="relative">
            <button
              onClick={() => {
                showToast("Aura premium offline artificial intelligence engine initialized! Standard text formats optimized.", 'info');
                logActivity('Optimized document text format layout parameters via Aura.');
              }}
              className="p-1.5 text-purple-700 hover:bg-purple-100/70 rounded-full transition-transform active:scale-90 cursor-pointer flex items-center justify-center"
              title="Aura intelligence tool"
            >
              <Sparkles className="w-4.5 h-4.5 animate-pulse" />
            </button>
            {/* New Violet/Purple premium Badge indicator */}
            <span className="absolute -top-1.5 -right-3.5 bg-indigo-600 text-white font-extrabold text-[7.5px] px-1 py-0.25 rounded-md uppercase select-none tracking-wide shadow animate-bounce">
              New
            </span>
          </div>

          {/* spelling/grammar layout option (abc ✓) */}
          <button
            onClick={() => {
              showToast("Spellcheck diagnostic scan: NO grammar or typo issues found!", 'success');
              logActivity('Successfully ran full Spellcheck verification diagnostics.');
            }}
            className="p-1.5 text-slate-700 hover:bg-black/5 rounded-full transition-transform active:scale-95 text-xs font-bold flex items-center gap-0.5"
            title="Grammar spellcheck scanner"
          >
            abc
            <ChevronDown className="w-3 h-3 text-slate-500" />
          </button>

          {/* Calendar alarm clock config toggle */}
          <div className="relative">
            <button
              onClick={() => {
                setShowReminderConfig(!showReminderConfig);
                setShowBrushSelector(false);
                setShowTagSelector(false);
              }}
              className={`p-1.5 rounded-full transition-all flex items-center justify-center cursor-pointer ${showReminderConfig ? 'bg-indigo-500/10 text-indigo-700' : 'text-slate-700 hover:bg-black/5'}`}
              title="Schedule Alarm Notification"
            >
              <Clock className="w-4.5 h-4.5" />
            </button>

            {/* Reminder Config popup panel */}
            {showReminderConfig && (
              <div className="absolute bottom-11 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-200/95 rounded-2xl w-[260px] p-4 shadow-2xl block animate-in zoom-in-95 duration-100">
                <span className="text-[10px] font-bold text-slate-400 block mb-1.5 uppercase">Schedule Alarm Notification</span>
                <input
                  type="datetime-local"
                  value={reminderTime}
                  onChange={(e) => {
                    setReminderTime(e.target.value);
                    triggerPushUpdate({ reminderTime: e.target.value ? new Date(e.target.value).getTime() : undefined });
                    logActivity(e.target.value ? `Rescheduled alarm timer offset: ${e.target.value}.` : 'Cleared alarm schedule.');
                  }}
                  className="text-xs bg-slate-50 p-2.5 border rounded-lg w-full font-semibold mb-2.5 text-slate-800"
                />
                
                {reminderTime && (
                  <button 
                    onClick={() => {
                      setReminderTime('');
                      triggerPushUpdate({ reminderTime: undefined });
                      logActivity('Cleared alarm notification.');
                    }}
                    className="text-[9.5px] text-rose-500 font-bold block mb-2 hover:underline"
                  >
                    Clear scheduled alarm
                  </button>
                )}
                
                <div className="flex justify-end pt-1.5 border-t">
                  <button 
                    onClick={() => setShowReminderConfig(false)} 
                    className="bg-zinc-800 hover:bg-black text-white px-3 py-1 bg-zinc-900 rounded text-[10.5px] font-bold"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Designer desk watercolor brushed picker */}
          <div className="relative">
            <button
              onClick={() => {
                setShowBrushSelector(!showBrushSelector);
                setShowReminderConfig(false);
                setShowTagSelector(false);
              }}
              className={`p-1.5 rounded-full transition-all flex items-center justify-center cursor-pointer ${showBrushSelector ? 'bg-amber-150 text-amber-700' : 'text-slate-700 hover:bg-black/5'}`}
              title="Desk color design presets"
            >
              <Palette className="w-4.5 h-4.5" />
            </button>

            {/* Pastel palettes selectors */}
            {showBrushSelector && (
              <div className="absolute bottom-11 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-250 rounded-2xl w-[270px] p-4 shadow-2xl block animate-in duration-100">
                <div className="flex items-center justify-between pb-2 border-b mb-3 text-xs font-bold text-slate-800">
                  <span>Desk Colors & Presets</span>
                  <button onClick={() => setShowBrushSelector(false)} className="text-indigo-600 hover:underline">Done</button>
                </div>

                {/* Swatches array grids */}
                <div className="grid grid-cols-4 gap-2">
                  {DESK_THEMES.map((th, i) => (
                    <button
                      key={th.name}
                      onClick={() => {
                        setDeskThemeIdx(i);
                        triggerPushUpdate({ deskThemeIdx: i, boardColor: th.bgValue });
                        logActivity(`Changed write workspace desk setting to: "${th.name}".`);
                      }}
                      className={`aspect-video rounded-lg border-2 text-[9.5px] font-extrabold flex items-center justify-center p-1 transition-all ${deskThemeIdx === i ? 'border-indigo-600 scale-105 shadow-xs' : 'border-slate-200'}`}
                      style={{ backgroundColor: th.bgValue, color: th.textValue }}
                    >
                      {deskThemeIdx === i && "✓"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Labels organizing tagged button */}
          <div className="relative">
            <button
              onClick={() => {
                setShowTagSelector(!showTagSelector);
                setShowReminderConfig(false);
                setShowBrushSelector(false);
              }}
              className={`p-1.5 rounded-full transition-all flex items-center justify-center cursor-pointer ${showTagSelector ? 'bg-emerald-500/15 text-emerald-700' : 'text-slate-700 hover:bg-black/5'}`}
              title="Organize Folder Labels"
            >
              <Tag className="w-4.5 h-4.5" />
            </button>

            {/* Tag config overlays popup */}
            {showTagSelector && (
              <div className="absolute bottom-11 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-250 rounded-2xl w-[270px] p-4 shadow-2xl block animate-in duration-100">
                <span className="text-[10px] font-bold text-slate-400 block mb-2 uppercase">Tags Organizer</span>
                <form onSubmit={handleAddTag} className="flex gap-2 mb-2.5">
                  <input
                    type="text"
                    placeholder="Create Tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    className="flex-1 bg-slate-50 border p-1.5 px-3 rounded-lg focus:outline-none text-xs text-slate-800"
                  />
                  <button type="submit" className="bg-zinc-800 text-white px-3 py-1.5 rounded-lg text-xs font-bold">Add</button>
                </form>

                <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1 mb-2.5 select-none text-[10px]">
                  {allTags.filter(t => !tags.includes(t)).slice(0, 8).map((tVal) => (
                    <button
                      key={tVal}
                      type="button"
                      onClick={() => handleSelectPreexistingTag(tVal)}
                      className="bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 px-2 py-1 rounded-full font-semibold border"
                    >
                      + {tVal}
                    </button>
                  ))}
                </div>

                <div className="flex justify-end pt-1.5 border-t">
                  <button onClick={() => setShowTagSelector(false)} className="bg-zinc-800 text-white px-3 py-1 rounded text-[10.5px] font-bold">Done</button>
                </div>
              </div>
            )}
          </div>

          {/* Collaborative Share member insertion button */}
          <button
            onClick={() => {
              showToast("Entering collaborative drafting: Workspace share configurations initialized!", 'info');
              logActivity('Configured team shared workspace parameters.');
            }}
            className="p-1.5 text-slate-700 hover:bg-black/5 rounded-full transition-transform active:scale-95 cursor-pointer flex items-center justify-center"
            title="Invite collaborators"
          >
            <Users className="w-4.5 h-4.5" />
          </button>

          {/* Export / local file share button */}
          <button
            onClick={() => {
              handleCopyToClipboard();
              showToast("Export summary saved successfully as standard markup document!", 'success');
              logActivity('Successfully exported active note draft.');
            }}
            className="p-1.5 text-slate-700 hover:bg-black/5 rounded-full transition-transform active:scale-95 cursor-pointer flex items-center justify-center"
            title="Export / Share note"
          >
            <Share2 className="w-4.5 h-4.5" />
          </button>

          {/* Three dots advanced settings trigger button (Summons the scrollable dropdown) */}
          <div className="relative">
            <button
              onClick={() => setShowMenuPopover(!showMenuPopover)}
              className={`p-1.5 rounded-full transition-all flex items-center justify-center cursor-pointer hover:bg-black/10 text-slate-800`}
              title="More Actions Settings"
              id="write-section-advanced-settings-dots"
            >
              <span className="text-sm font-extrabold pb-0.5 tracking-tighter">•••</span>
            </button>

            {/* --- 12-ITEM SCROLLABLE CHARCOAL DARK DROPDOWN POPUP MENU (SCREENSHOTS 0 & 2 SPECIFICATION) --- */}
            {showMenuPopover && (
              <div 
                className="absolute bottom-11 right-0 z-50 bg-[#242629]/95 backdrop-blur-md border border-zinc-700/60 rounded-xl shadow-2xl w-60 py-2 block select-none animate-in duration-100"
                id="three-dots-charcoal-options-dropdown"
              >
                {/* Scrollable list frame */}
                <div className="max-h-[260px] overflow-y-auto text-xs font-semibold text-zinc-100 pr-1 select-none auto-scrollbar">
                  
                  {/* Item 1: Info */}
                  <button
                    onClick={() => {
                      setActiveDialog('info');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Info className="w-4 h-4 text-sky-400 shrink-0" />
                    Info
                  </button>

                  {/* Item 2: Versions */}
                  <button
                    onClick={() => {
                      setActiveDialog('versions');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <History className="w-4 h-4 text-amber-400 shrink-0" />
                    Versions
                  </button>

                  {/* Item 3: Note Activities */}
                  <button
                    onClick={() => {
                      setActiveDialog('activities');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Activity className="w-4 h-4 text-emerald-400 shrink-0" />
                    Note Activities
                  </button>

                  {/* Item 4: Favorite */}
                  <button
                    onClick={handleFavoriteAction}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <svg 
                      className={`w-4 h-4 shrink-0 ${card.isFavorite ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {card.isFavorite ? "Unfavorite" : "Favorite"}
                  </button>

                  {/* Item 5: Note Merge */}
                  <button
                    onClick={() => {
                      loadMergeNotes();
                      setActiveDialog('merge');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <svg className="w-4 h-4 text-indigo-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Note Merge
                  </button>

                  {/* Item 6: Lock security passcode configuration */}
                  <button
                    onClick={() => {
                      setActiveDialog('lock');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Lock className="w-4 h-4 text-yellow-500 shrink-0" />
                    Lock
                  </button>

                  {/* Item 7: Copy Content */}
                  <button
                    onClick={handleCopyToClipboard}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Copy className="w-4 h-4 text-teal-400 shrink-0" />
                    Copy to Clipboard
                  </button>

                  {/* Item 8: Move To Notebook directory lists */}
                  <button
                    onClick={() => {
                      setActiveDialog('move');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <svg className="w-4 h-4 text-violet-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    Move to Notebook
                  </button>

                  {/* Item 9: Print document layout sheet */}
                  <button
                    onClick={handlePrintDraft}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Printer className="w-4 h-4 text-slate-400 shrink-0" />
                    Print Note
                  </button>

                  {/* Item 10: Associated referenced URL notes */}
                  <button
                    onClick={() => {
                      setActiveDialog('associated_links');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Link className="w-4 h-4 text-sky-400 shrink-0" />
                    Associated Links
                  </button>

                  {/* Item 11: Internals query terms searching */}
                  <button
                    onClick={() => {
                      setActiveDialog('search');
                      setShowMenuPopover(false);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-700 flex items-center gap-3 transition-colors text-zinc-200"
                  >
                    <Search className="w-4 h-4 text-rose-450 shrink-0" />
                    Search Text
                  </button>

                  {/* Item 12: Trash Archive recycler bin move action */}
                  <button
                    onClick={async (e) => {
                      if (await confirm("Move this entire Note Card to Recycle Bin?")) {
                        onDelete(card.id!, e);
                        onCancel();
                      }
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-rose-950 hover:text-rose-300 flex items-center gap-3 transition-colors text-rose-400 border-t border-zinc-700/60 mt-1"
                  >
                    <Trash2 className="w-4 h-4 shrink-0" />
                    Trash
                  </button>

                </div>

                {/* Pointing caret arrow matches layout screenshots */}
                <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-[#242629] rotate-45 border-r border-b border-zinc-700/60" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========================================== */}
      {/* --- ALL COMPANION SUB-DIALOG OVERLAYS --- */}
      {/* ========================================== */}

      {/* Dialog 1: Meta-Information Center Modal */}
      {activeDialog === 'info' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 select-none">
          <div className="bg-white border rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-800">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <span className="text-[14px] font-extrabold text-slate-900 flex items-center gap-2">
                <Info className="w-4 h-4 text-indigo-600" />
                Note Meta-Information
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 hover:bg-slate-100 rounded text-slate-500 font-bold">×</button>
            </div>

            <div className="space-y-2.5 text-xs font-semibold">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Document Type</span>
                <span className="capitalize text-slate-800">{card.type} Note</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Word Count</span>
                <span className="text-slate-850">{wordCount} words</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Character Length</span>
                <span className="text-slate-850">{charCount} characters</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Estimated read duration</span>
                <span className="text-slate-850">{wordCount > 0 ? `${Math.ceil(wordCount / 180)} min` : '< 1 min'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Date Created</span>
                <span className="text-slate-850">{new Date(card.createdAt || Date.now()).toLocaleDateString()} {new Date(card.createdAt || Date.now()).toLocaleTimeString()}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Last Synced modification</span>
                <span className="text-slate-850">{new Date(card.updatedAt || Date.now()).toLocaleDateString()} {new Date(card.updatedAt || Date.now()).toLocaleTimeString()}</span>
              </div>
            </div>

            <button 
              onClick={() => setActiveDialog(null)} 
              className="mt-5 w-full bg-zinc-900 hover:bg-black text-white py-2.5 rounded-xl text-xs font-bold transition-transform active:scale-95 shadow"
            >
              Close Info Frame
            </button>
          </div>
        </div>
      )}

      {/* Dialog 2: Version History snapshots manager */}
      {activeDialog === 'versions' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border text-left rounded-2xl w-full max-w-lg p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-800">
            <div className="flex items-center justify-between pb-3 border-b mb-4 select-none">
              <span className="text-[14px] font-extrabold text-slate-900 flex items-center gap-2">
                <History className="w-4 h-4 text-amber-500" />
                Draft Version Backups
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 hover:bg-slate-100 rounded text-slate-500 font-extrabold">×</button>
            </div>

            <div className="grid grid-cols-5 gap-4">
              {/* Backups select list column */}
              <div className="col-span-2 space-y-1.5 max-h-[280px] overflow-y-auto auto-scrollbar pr-1 select-none">
                {versionSnapshots.map((snap, idx) => (
                  <button
                    key={snap.id}
                    onClick={() => setSelectedSnapshotId(snap.id)}
                    className={`w-full text-left p-2 rounded-xl text-[10.5px] font-bold border transition-colors ${
                      selectedSnapshotId === snap.id || (idx === 0 && !selectedSnapshotId)
                        ? 'bg-amber-100/75 border-amber-300 text-amber-900' 
                        : 'hover:bg-slate-50 border-slate-100 text-slate-600'
                    }`}
                  >
                    <div className="truncate font-bold mb-0.5">{snap.title || 'Untitled Snapshot'}</div>
                    <div className="text-[8.5px] text-slate-450 font-mono font-medium">{new Date(snap.timestamp).toLocaleTimeString()}</div>
                  </button>
                ))}
                
                {versionSnapshots.length === 0 && (
                  <div className="text-center text-slate-400 py-6 text-[10px]">No snapshots cataloged.</div>
                )}
              </div>

              {/* Version preview column */}
              <div className="col-span-3 border rounded-xl p-3 bg-slate-50 max-h-[280px] overflow-y-auto flex flex-col justify-between">
                <div>
                  <span className="text-[9px] font-bold text-slate-450 uppercase block mb-1">Backup Snapshot Review</span>
                  {(() => {
                    const activeSnap = versionSnapshots.find(s => s.id === selectedSnapshotId) || versionSnapshots[0];
                    if (!activeSnap) return <div className="text-xs text-slate-400">Empty</div>;
                    return (
                      <div className="text-xs">
                        <div className="font-bold underline text-slate-800 mb-1">{activeSnap.title}</div>
                        <div 
                          className="text-[11px] text-slate-655 leading-relaxed break-words"
                          dangerouslySetInnerHTML={{ __html: activeSnap.textContent }}
                        />
                        {activeSnap.checklist && activeSnap.checklist.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {activeSnap.checklist.map(item => (
                              <div key={item.id} className="text-[10px] text-slate-500 font-semibold">
                                {item.completed ? '✓' : '☐'} {item.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="pt-2 border-t mt-3 select-none">
                  <button
                    onClick={() => {
                      const activeSnap = versionSnapshots.find(s => s.id === selectedSnapshotId) || versionSnapshots[0];
                      if (activeSnap) handleRestoreSnapshot(activeSnap);
                    }}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-amber-955 text-xs font-bold py-2 rounded-xl transition-transform active:scale-95 shadow-sm"
                  >
                    Restore This Version
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog 3: Note Activities ledger */}
      {activeDialog === 'activities' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 select-none">
          <div className="bg-white border rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-800">
            <div className="flex items-center justify-between pb-3 border-b mb-3">
              <span className="text-[14px] font-extrabold text-slate-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                Note Activity Logs
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 hover:bg-slate-100 rounded text-slate-500 font-extrabold">×</button>
            </div>

            <div className="text-xs font-semibold text-slate-500 flex justify-between pb-2.5 border-b mb-3">
              <span>Elapsed Session Duration</span>
              <span className="font-mono text-slate-800">{Math.floor(secondsElapsed / 60)}m {secondsElapsed % 60}s</span>
            </div>

            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 text-[11px] auto-scrollbar mb-4">
              {activityLogs.map((log, i) => (
                <div key={i} className="flex gap-2.5 items-start p-1.5 bg-slate-50 rounded-lg">
                  <span className="font-mono text-[9px] text-slate-400 pt-0.5">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  <p className="font-bold text-slate-700 flex-1 leading-normal">{log.text}</p>
                </div>
              ))}
            </div>

            <button onClick={() => setActiveDialog(null)} className="w-full bg-zinc-900 hover:bg-black text-white text-xs font-bold py-2.5 rounded-xl shadow transition-transform active:scale-95">
              Done
            </button>
          </div>
        </div>
      )}

      {/* Dialog 4: Lock config setup */}
      {activeDialog === 'lock' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 select-none text-slate-800">
          <div className="bg-white border rounded-2xl w-full max-w-xs p-5 shadow-2xl text-center animate-in duration-100">
            <div className="w-12 h-12 bg-yellow-500/10 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3.5">
              <Lock className="w-6 h-6" />
            </div>
            
            {pinCode ? (
              <div>
                <h4 className="text-md font-bold text-slate-950 tracking-tight">Active lock PIN</h4>
                <p className="text-xs text-slate-500 mt-1 mb-5">This draft is locked. Unlock it below.</p>
                <button
                  onClick={handleUnlockDocument}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs py-2.5 rounded-xl transition-all"
                >
                  Unlock Document
                </button>
              </div>
            ) : (
              <div>
                <h4 className="text-md font-bold text-slate-950 tracking-tight">Set Security Code</h4>
                <p className="text-xs text-slate-500 mt-1 mb-4">Assign a 4-digit PIN code to restrict viewers.</p>
                
                <input
                  type="password"
                  maxLength={4}
                  placeholder="••••"
                  value={lockPinInput}
                  onChange={(e) => setLockPinInput(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-32 tracking-widest text-[16px] font-mono text-center p-2 mb-4 border rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500 bg-slate-50 select-text text-slate-800"
                />

                <div className="flex gap-2 select-none justify-center">
                  <button onClick={() => setActiveDialog(null)} className="px-4 py-2 border rounded-xl hover:bg-slate-100 text-xs font-bold text-slate-500">Cancel</button>
                  <button onClick={handleLockSetup} className="px-4 py-2 bg-zinc-900 border border-zinc-900 text-white hover:bg-black rounded-xl text-xs font-bold transition-transform active:scale-95">Set Lock</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog 5: Merge Document slider popup */}
      {activeDialog === 'merge' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-800 select-none text-left">
            <div className="flex items-center justify-between pb-3 border-b mb-3">
              <span className="text-[14px] font-extrabold text-slate-900 flex items-center gap-2">
                Conjoin / Merge Note Cards
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 text-slate-500 font-extrabold">×</button>
            </div>

            <p className="text-[11px] text-slate-500 mb-3.5 italic leading-snug">Conjoining note documents aggregates paragraphs and checklist schedules altogether instantly.</p>
            
            <div className="space-y-1.5 max-h-48 overflow-y-auto auto-scrollbar pr-1 mb-4 select-none">
              {availableNotesForMerge.map((note) => (
                <button
                  key={note.id}
                  onClick={() => setSelectedNoteIdToMerge(note.id)}
                  className={`w-full p-2.5 text-left border rounded-xl flex justify-between items-center transition-colors font-bold ${
                    selectedNoteIdToMerge === note.id ? 'border-indigo-650 bg-indigo-50 text-indigo-900' : 'hover:bg-slate-50 border-slate-100'
                  }`}
                >
                  <div className="truncate pr-4">
                    <div className="text-xs truncate">{note.title || 'Untitled Note'}</div>
                    <div className="text-[9px] text-slate-400 capitalize font-mono font-medium">{note.type} type</div>
                  </div>
                  {selectedNoteIdToMerge === note.id && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
                </button>
              ))}

              {availableNotesForMerge.length === 0 && (
                <p className="text-center text-[11px] text-slate-400 py-6">No alternate draft notes available in notebook bookshelf.</p>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setActiveDialog(null)} className="px-4 py-2 border rounded-xl hover:bg-slate-50 text-xs font-bold text-slate-550">Cancel</button>
              <button 
                onClick={handleMergeNotesAction} 
                disabled={!selectedNoteIdToMerge}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold disabled:opacity-30 transition-transform active:scale-95"
              >
                Conjoin Notes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog 6: Move To Notebook folder cover index */}
      {activeDialog === 'move' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-850 select-none text-left">
            <div className="flex items-center justify-between pb-3 border-b mb-3">
              <span className="text-[14px] font-extrabold text-slate-950 flex items-center gap-1.5">
                Migrate Draft Folder Location
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <p className="text-[11px] text-slate-500 mb-4 leading-normal font-sans">Choose which shelf folder directory to target for storage migration.</p>

            <div className="space-y-1.5 max-h-48 overflow-y-auto auto-scrollbar pr-1 mb-4">
              {notebooks.filter(b => b.id !== card.notebookId).map((nb) => (
                <button
                  key={nb.id}
                  onClick={() => handleMoveNoteToNotebook(nb.id)}
                  className="w-full p-2.5 hover:bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between transition-colors text-xs font-extrabold text-slate-800"
                >
                  <span className="truncate">{nb.title}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90 shrink-0" />
                </button>
              ))}

              {notebooks.filter(b => b.id !== card.notebookId).length === 0 && (
                <p className="text-center text-[10.5px] text-slate-400 py-6">No alternate notebook binders available on bookshelf.</p>
              )}
            </div>

            <button onClick={() => setActiveDialog(null)} className="w-full border rounded-xl py-2 hover:bg-slate-50 text-xs font-bold text-slate-500">Cancel</button>
          </div>
        </div>
      )}

      {/* Dialog 7: Associated Links referenced cataloger */}
      {activeDialog === 'associated_links' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border text-left rounded-2xl w-full max-w-sm p-5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-800 select-none">
            <div className="flex items-center justify-between pb-3 border-b mb-3">
              <span className="text-[14px] font-extrabold text-slate-900 flex items-center gap-1.5">
                Associated Reference Linkages
              </span>
              <button onClick={() => setActiveDialog(null)} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Paste Reference URL (e.g. google.com)..."
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                className="flex-1 bg-slate-50 border p-2 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 select-text"
              />
              <button 
                onClick={handleAddAssociatedLink} 
                className="bg-indigo-600 font-bold hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs transition-transform active:scale-95 shrink-0"
              >
                Add Link
              </button>
            </div>

            <div className="space-y-1 max-h-36 overflow-y-auto auto-scrollbar pr-1 mb-4 select-text">
              {associatedLinks.map((lnk, i) => (
                <div key={i} className="flex justify-between items-center bg-slate-50 p-2 rounded-xl border text-[11px] font-semibold text-slate-700">
                  <span className="truncate pr-4 underline text-indigo-600 block">{lnk}</span>
                  <button 
                    onClick={() => setAssociatedLinks(prev => prev.filter((_, idx) => idx !== i))}
                    className="p-1 text-slate-400 hover:text-rose-500 font-bold select-none cursor-pointer"
                  >
                    ×
                  </button>
                </div>
              ))}

              {associatedLinks.length === 0 && (
                <p className="text-center text-[10.5px] text-slate-400 py-4 font-normal">No attached reference URL linkages indexed yet.</p>
              )}
            </div>

            <button onClick={() => setActiveDialog(null)} className="w-full bg-zinc-900 text-white py-2.5 rounded-xl text-xs font-bold hover:bg-black transition-transform active:scale-95 shadow">Done</button>
          </div>
        </div>
      )}

      {/* Dialog 8: Internal Search highlighting bar */}
      {activeDialog === 'search' && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border rounded-2xl w-full max-w-sm p-4 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-850 select-none text-left">
            <div className="flex items-center justify-between pb-2 border-b mb-3">
              <span className="text-[13.5px] font-extrabold text-slate-900 flex items-center gap-1.5">
                Internal Text String Search
              </span>
              <button onClick={() => { setActiveDialog(null); handlePerformInternalSearch(''); }} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <div className="relative mb-2.5">
              <input
                type="text"
                placeholder="Type query terms to match..."
                value={searchQuery}
                onChange={(e) => handlePerformInternalSearch(e.target.value)}
                className="w-full bg-slate-50 border p-2.5 px-3 rounded-xl text-xs text-slate-800 focus:outline-none select-text"
                autoFocus
              />
              <Search className="w-4 h-4 text-slate-400 absolute right-3.5 top-3" />
            </div>

            <div className="flex justify-between items-center text-[11px] font-bold text-slate-500 mb-3 bg-slate-100 p-2 rounded-xl">
              <span>Matches Found</span>
              <span className={`px-2 py-0.5 rounded-md ${searchMatches > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-200'}`}>
                {searchMatches} instances
              </span>
            </div>

            <button 
              onClick={() => { setActiveDialog(null); handlePerformInternalSearch(''); }} 
              className="w-full bg-zinc-900 hover:bg-black text-white py-2 rounded-xl text-xs font-bold transition-transform active:scale-95 shadow"
            >
              Close Finder Frame
            </button>
          </div>
        </div>
      )}

      {/* Hyperlink Dialog Overlay */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 rounded-2xl w-full max-w-sm p-4.5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-850 select-none text-left">
            <div className="flex items-center justify-between pb-2.5 border-b dark:border-zinc-800 mb-3.5">
              <span className="text-[14px] font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                🔗 Insert Inline Hyperlink
              </span>
              <button onClick={() => setIsLinkModalOpen(false)} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 block mb-1 uppercase tracking-wider">Link Label text</label>
                <input
                  type="text"
                  placeholder="Insert link display text (e.g., Click here)..."
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border dark:border-zinc-700 p-2.5 rounded-xl text-xs text-slate-800 dark:text-zinc-150 focus:outline-none focus:ring-1 focus:ring-indigo-500 select-text font-semibold"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 block mb-1 uppercase tracking-wider">Target URL Address</label>
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border dark:border-zinc-700 p-2.5 rounded-xl text-xs text-slate-800 dark:text-zinc-150 focus:outline-none focus:ring-1 focus:ring-indigo-500 select-text font-mono"
                />
              </div>
            </div>

            {linkError && (
              <p className="text-rose-500 text-[10.5px] font-bold mt-1.5 px-1 animate-pulse">
                ⚠️ {linkError}
              </p>
            )}

            <div className="flex justify-between items-center mt-5 pt-3.5 border-t dark:border-zinc-800">
              <div>
                {isEditingLink && (
                  <button 
                    onClick={handleRemoveLink} 
                    className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/20 hover:dark:bg-rose-950/40 text-rose-600 dark:text-rose-450 rounded-xl text-xs font-bold transition-transform active:scale-95 border border-rose-100 dark:border-rose-900"
                  >
                    Remove Link
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsLinkModalOpen(false)} 
                  className="px-4 py-2 border dark:border-zinc-800 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 text-xs font-bold text-slate-550 dark:text-slate-400"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmInsertLink} 
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold transition-transform active:scale-95 shadow-md"
                >
                  {isEditingLink ? 'Update' : 'Insert'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note Link Search Modal */}
      {isNoteLinkModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 rounded-2xl w-full max-w-sm p-4.5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-850 select-none text-left">
            <div className="flex items-center justify-between pb-2.5 border-b dark:border-zinc-800 mb-3.5">
              <span className="text-[14px] font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                📄 Link to Another Note
              </span>
              <button onClick={() => setIsNoteLinkModalOpen(false)} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Search note titles..."
                value={noteLinkSearchQuery}
                onChange={(e) => setNoteLinkSearchQuery(e.target.value)}
                className="w-full bg-slate-50 dark:bg-zinc-800 border dark:border-zinc-700 p-2.5 px-3.5 rounded-xl text-xs text-slate-800 dark:text-zinc-150 focus:outline-none focus:ring-1 focus:ring-indigo-500 select-text font-semibold"
                autoFocus
              />
              <Search className="w-4 h-4 text-slate-405 absolute right-3.5 top-3" />
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1.5 mb-4 pr-1">
              {getNotesCatalog()
                .filter(n => n.id !== card.id && !n.isDeleted && (n.title || '').toLowerCase().includes(noteLinkSearchQuery.toLowerCase()))
                .map((targetCard) => (
                  <button
                    key={targetCard.id}
                    onMouseDown={(e) => { e.preventDefault(); handleInsertNoteLink(targetCard); }}
                    className="w-full p-2 text-left rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-xs font-bold text-slate-800 dark:text-zinc-200 flex justify-between items-center"
                  >
                    <span className="truncate pr-4">{targetCard.title || 'Untitled Note'}</span>
                    <span className="text-[10px] text-slate-400 shrink-0 font-mono capitalize">{targetCard.type}</span>
                  </button>
                ))}
              {getNotesCatalog().filter(n => n.id !== card.id && !n.isDeleted && (n.title || '').toLowerCase().includes(noteLinkSearchQuery.toLowerCase())).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-400 font-medium">No matching notes found.</div>
              )}
            </div>

            <div className="flex justify-end pt-3 border-t dark:border-zinc-800">
              <button
                onClick={() => setIsNoteLinkModalOpen(false)}
                className="px-4 py-2 border dark:border-zinc-800 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 text-xs font-bold text-slate-550 dark:text-slate-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed URL Modal */}
      {isEmbedModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 rounded-2xl w-full max-w-sm p-4.5 shadow-2xl animate-in zoom-in-95 duration-100 text-slate-850 select-none text-left">
            <div className="flex items-center justify-between pb-2.5 border-b dark:border-zinc-800 mb-3.5">
              <span className="text-[14px] font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                🌐 Embed Video or Link
              </span>
              <button onClick={() => setIsEmbedModalOpen(false)} className="p-1 font-extrabold text-slate-450 hover:text-black">×</button>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 block mb-1 uppercase tracking-wider">Embed Target URL</label>
                <input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border dark:border-zinc-700 p-2.5 rounded-xl text-xs text-slate-800 dark:text-zinc-150 focus:outline-none focus:ring-1 focus:ring-indigo-500 select-text font-mono"
                  autoFocus
                />
              </div>
            </div>

            {embedError && (
              <p className="text-rose-500 text-[10.5px] font-bold mt-1.5 px-1 animate-pulse">
                ⚠️ {embedError}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-5 pt-3 border-t dark:border-zinc-800">
              <button
                onClick={() => setIsEmbedModalOpen(false)}
                className="px-4 py-2 border dark:border-zinc-800 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 text-xs font-bold text-slate-550 dark:text-slate-400"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertEmbed}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold transition-transform active:scale-95 shadow-md"
              >
                Insert Embed
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Internal missing Icon definitions so component is robustly self-contained with zero compile errors:
const Table2Icon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="1em" 
    height="1em" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    {...props}
  >
    <path d="M12 3v18" />
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M3 12h18" />
  </svg>
);
